(function () {
    const SERP_ARTICLES_INDEX = "production_articles";
    const COUNTRY_TO_CURRENCY_FALLBACK = {
        US: "USD",
        CA: "CAD",
        GB: "GBP",
        UK: "GBP",
        JP: "JPY",
    };

    const isPlpSortDebugEnabled = () => {
        if (typeof window === "undefined") return false;

        try {
            const url = new URL(window.location.href);
            const debugParam = url.searchParams.get("po_sort_debug");

            if (debugParam === "1" || debugParam === "true") {
                sessionStorage.setItem("po_sort_debug", "1");
                window.__PO_PLP_SORT_DEBUG__ = true;
                return true;
            }

            if (debugParam === "0" || debugParam === "false") {
                sessionStorage.removeItem("po_sort_debug");
                window.__PO_PLP_SORT_DEBUG__ = false;
                return false;
            }
        } catch (_error) {
            // no-op
        }

        if (window.__PO_PLP_SORT_DEBUG__ === true) {
            return true;
        }

        try {
            return sessionStorage.getItem("po_sort_debug") === "1";
        } catch (_error) {
            return false;
        }
    };

    const poPlpSortDebug = (scope, payload = {}) => {
        if (!isPlpSortDebugEnabled()) {
            return;
        }

        window.poPlpSortDebugTools?.record("algolia", scope, payload);
        console.log(`[PO sort debug][algolia] ${scope}`, payload);
    };

    const applySearchFilteringTealiumFields = (payload, facetValues) => {
        const helper = typeof window !== "undefined"
            ? window.POTealiumEventDedupe?.applySearchFilteringFields
            : null;

        if (typeof helper === "function") {
            return helper(payload, facetValues);
        }

        if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
            return payload;
        }

        const normalizedFacetValues = String(facetValues || "").trim();

        delete payload.Events_SearchFiltering;
        delete payload.Search_FacetValues_String;

        if (normalizedFacetValues) {
            payload.Events_SearchFiltering = "1";
            payload.Search_FacetValues_String = normalizedFacetValues;
        }

        return payload;
    };

    const getSortDebugParamsSummary = (rawParams = "") => {
        try {
            return Object.fromEntries(new URLSearchParams(rawParams).entries());
        } catch (_error) {
            return { rawParams };
        }
    };

    const collectSortDebugPriceData = (value, path = "", depth = 0, acc = {}) => {
        if (!value || typeof value !== "object" || Array.isArray(value) || depth > 2) {
            return acc;
        }

        Object.entries(value).forEach(([key, nestedValue]) => {
            const nextPath = path ? `${path}.${key}` : key;
            const isPricePath = /price/i.test(nextPath);

            if (nestedValue && typeof nestedValue === "object" && !Array.isArray(nestedValue)) {
                if (isPricePath) {
                    acc[nextPath] = nestedValue;
                }
                collectSortDebugPriceData(nestedValue, nextPath, depth + 1, acc);
                return;
            }

            if (isPricePath) {
                acc[nextPath] = nestedValue;
            }
        });

        return acc;
    };

    const getSortDebugHitSummary = (hit = {}) => ({
        handle: hit?.handle || null,
        objectID: hit?.objectID || null,
        groupKey: hit?.meta?.custom?.x_groupkey || null,
        priceData: collectSortDebugPriceData(hit),
    });

    const getSortDebugHitLine = (hit = {}) => {
        const summary = getSortDebugHitSummary(hit);
        const priceSummary = Object.entries(summary.priceData || {})
            .slice(0, 8)
            .map(([path, value]) => {
                const normalizedValue =
                    value && typeof value === "object" ? JSON.stringify(value) : String(value);
                return `${path}=${normalizedValue}`;
            })
            .join(" | ");

        return `${summary.handle || "unknown"}${summary.groupKey ? ` [${summary.groupKey}]` : ""}${priceSummary ? ` :: ${priceSummary}` : ""}`;
    };

    const normalizeSortOrderValue = (value) => {
        if (typeof value !== "string") return "";
        const normalized = value.trim().toLowerCase();
        const markerIndex = normalized.indexOf("products");
        return markerIndex >= 0 ? normalized.slice(markerIndex) : normalized;
    };

    const filterSortOrdersByAllowlist = (options) => {
        if (!Array.isArray(options)) return options;
        const allowlist = window.poAlgoliaSortOrderAllowlist;
        if (!Array.isArray(allowlist) || allowlist.length === 0) return options;

        const allowed = new Set(allowlist.map(normalizeSortOrderValue).filter(Boolean));
        const filtered = options.filter((option) => {
            const value = typeof option === "string" ? option : option?.value;
            const normalized = normalizeSortOrderValue(value);
            return normalized && allowed.has(normalized);
        });

        return filtered.length > 0 ? filtered : options;
    };

    const decodeHtmlEntities = (() => {
        let textarea;

        return (value) => {
            if (typeof value !== "string" || !value.includes("&") || typeof document === "undefined") {
                return value;
            }

            textarea ||= document.createElement("textarea");
            textarea.innerHTML = value;
            return textarea.value;
        };
    })();

    const getAlgoliaTranslations = () =>
        Object.fromEntries(
            Object.entries({
                ...(window.algolia?.translations || {}),
                ...(window.algoliaShopify?.translations || {}),
                ...(window.poAlgoliaTranslationsOverrides || {}),
            }).map(([key, value]) => [key, decodeHtmlEntities(value)])
        );

    const FACET_RESULTS_CACHE_LIMIT = 24;
    const RESULT_COUNT_CACHE_LIMIT = 48;
    const PRICE_SORT_MARKET_ATTRIBUTES = [
        "market_pricing_usd",
        "market_pricing_eur",
        "market_pricing_cad",
        "market_pricing_jpy",
        "market_pricing_gbp",
    ];
    const MINIMAL_PRODUCT_HIT_ATTRIBUTES = JSON.stringify([
        "handle",
        "title",
        "id",
        "image",
        "collections",
        "inventory_available",
        "meta",
    ]);
    const PRICE_SORT_PRODUCT_HIT_ATTRIBUTES = JSON.stringify([
        "handle",
        "title",
        "id",
        "image",
        "collections",
        "inventory_available",
        "meta",
        "price",
        "compare_at_price",
        "price_range",
        "variants_min_price",
        "variants_max_price",
        ...PRICE_SORT_MARKET_ATTRIBUTES,
    ]);
    const MINIMAL_GROUP_HIT_ATTRIBUTES = JSON.stringify([
        "handle",
        "meta",
    ]);
    const PRICE_SORT_GROUP_HIT_ATTRIBUTES = JSON.stringify([
        "handle",
        "meta",
        "price",
        "compare_at_price",
        "price_range",
        "variants_min_price",
        "variants_max_price",
        ...PRICE_SORT_MARKET_ATTRIBUTES,
    ]);
    const EMPTY_HIT_ATTRIBUTES = JSON.stringify([]);
    const GROUP_RANKING_HIT_ATTRIBUTES = JSON.stringify(["handle"]);
    const ALGOLIA_MAX_QUERIES_PER_BATCH = 50;
    const compareNumericSortValues = (leftValue, rightValue, direction) => {
        if (!Number.isFinite(leftValue)) return Number.isFinite(rightValue) ? 1 : 0;
        if (!Number.isFinite(rightValue)) return -1;
        return direction === "desc"
            ? rightValue - leftValue
            : leftValue - rightValue;
    };

    const setBoundedMapValue = (map, key, value, limit) => {
        if (map.has(key)) {
            map.delete(key);
        }

        map.set(key, value);

        while (map.size > limit) {
            const oldestKey = map.keys().next().value;
            if (oldestKey === undefined) break;
            map.delete(oldestKey);
        }

        return value;
    };

    const escapeHtml = (value) =>
        String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const applyMinimalHitPayloadToParams = (params, { includePriceData = false } = {}) => {
        if (!params?.set) {
            return;
        }

        if (isPlpSortDebugEnabled()) {
            params.delete("attributesToRetrieve");
            params.delete("attributesToHighlight");
            params.delete("attributesToSnippet");
            params.set("getRankingInfo", "true");
            return;
        }

        params.set(
            "attributesToRetrieve",
            includePriceData ? PRICE_SORT_PRODUCT_HIT_ATTRIBUTES : MINIMAL_PRODUCT_HIT_ATTRIBUTES
        );
        params.set("attributesToHighlight", EMPTY_HIT_ATTRIBUTES);
        params.set("attributesToSnippet", EMPTY_HIT_ATTRIBUTES);
    };

    const applyMinimalHitPayloadToHelper = (helper, { includePriceData = false } = {}) => {
        if (!helper?.setQueryParameter) {
            return;
        }

        if (isPlpSortDebugEnabled()) {
            helper.setQueryParameter("attributesToRetrieve", undefined);
            helper.setQueryParameter("attributesToHighlight", undefined);
            helper.setQueryParameter("attributesToSnippet", undefined);
            helper.setQueryParameter("getRankingInfo", true);
            return;
        }

        helper.setQueryParameter(
            "attributesToRetrieve",
            includePriceData ? PRICE_SORT_PRODUCT_HIT_ATTRIBUTES : MINIMAL_PRODUCT_HIT_ATTRIBUTES
        );
        helper.setQueryParameter("attributesToHighlight", EMPTY_HIT_ATTRIBUTES);
        helper.setQueryParameter("attributesToSnippet", EMPTY_HIT_ATTRIBUTES);
    };

    const applyMinimalGroupPayloadToParams = (params, { includePriceData = false } = {}) => {
        if (!params?.set) {
            return;
        }

        if (isPlpSortDebugEnabled()) {
            params.delete("attributesToRetrieve");
            params.delete("attributesToHighlight");
            params.delete("attributesToSnippet");
            params.set("getRankingInfo", "true");
            return;
        }

        params.set(
            "attributesToRetrieve",
            includePriceData ? PRICE_SORT_GROUP_HIT_ATTRIBUTES : MINIMAL_GROUP_HIT_ATTRIBUTES
        );
        params.set("attributesToHighlight", EMPTY_HIT_ATTRIBUTES);
        params.set("attributesToSnippet", EMPTY_HIT_ATTRIBUTES);
    };

    class AlgoliaPoCustomSearch {
        constructor() {
            this.desktopProductsPerPage = Number(document.currentScript.dataset.itemsPerPage);
            this.mobileProductsPerPage = Number(document.currentScript.dataset.mobileItemsPerPage) || 4;
            this.desktopColumns = Number(document.currentScript?.dataset?.desktopColumns) || 0;
            this.template = document.currentScript?.dataset?.template || "collection";
            this.productsPerPage = this.getProductsPerPage();
            this.initialSearchInput = String(document.currentScript?.dataset?.searchInput || "").trim();
            this.useDynamicPlpGroupCards = this.template === "collection" && !this.isCollectionUngrouped();
            this.minResultsForBanners = parseInt(document.currentScript.dataset.minResultsForBanners) || 10;
            this.productsAdded = 0;
            this.firstPageRendered = false;
            this.totalRows = 0;
            this.fetchParams = undefined;
            this.loadMoreIndex = null;
            this.algoliaApiKey = window.algoliaShopify?.config?.search_api_key;
            this.algoliaAppId = window.algoliaShopify?.config?.app_id;
            this.facets = [];
            this.activeFacetFilters = [];
            this.sortOrders = filterSortOrdersByAllowlist(window.algoliaShopify?.sortOrders || []);
            this.articleIndexName = SERP_ARTICLES_INDEX;
            this.articlesLoaded = false;
            this._articlesLoadedForQuery = "";
            this.productHits = 0;
            this.totalHits = 0;
            this.articleCount = 0;
            this.hasResultStats = false;
            this.searchLoadMoreOffset = 0;
            this.displayProductHits = 0;
            this.searchLoadMoreExhausted = false;

            this.groupKeys = [];
            this.groupKeySet = new Set();
            this.ungroupedColorKeySet = new Set();
            this.lastNbHits = 0;
            this.lastMaxHits = 0;
            this.firstPageHits = [];
            this.cardPromises = [];
            this.filteredFacetsMap = new Map();
            this.filteredResultCountMap = new Map();
            this.lastFacetStr = "";
            this._lastSerpQuery = "";
            this.lastFiltersEventSignature = "";
            this.lastFiltersEventAt = 0;
            this._pendingSerpTealiumRetry = null;
            this.pendingCardControllers = new Set();
            this.pendingGroupController = null;
            this.pendingFacetController = null;
            this.pendingResultCountController = null;
            this.pendingCardSlots = new Map();
            this.pendingHydrationQueue = [];
            this.pendingResultsScrollFrame = null;
            this.activeCardHydrations = 0;
            this.maxConcurrentCardHydrations = this.template === "collection" ? 6 : 6;
            this.cardSlotObserverRootMargin = this.template === "collection" ? "1400px 0px" : "500px 0px";
            this.cardSlotObserver = null;
            this.cardSlotMetaMap = new Map();
            this.virtualCardObserver = null;
            this.virtualCardObserverRootMargin = this.template === "collection" ? "1800px 0px" : "0px";
            this.productRenderToken = 0;
            this.persistGroupsTimer = null;
            this.cardMarkupCache = new Map();
            this.cardMarkupCacheLimit = this.template === "collection" ? 72 : 24;

            this.initCardSlotObserver();
            this.initVirtualCardObserver();
            this.init();
        }

        isCollectionUngrouped() {
            if (this.template !== "collection") {
                return false;
            }

            const notGrouped = window.collection?.not_grouped;
            return notGrouped === true || notGrouped === "true" || notGrouped === 1 || notGrouped === "1";
        }

        getHitColorKey(hit = {}) {
            const colorCode = String(hit?.meta?.custom?.color_code || "").trim().toLowerCase();
            const frameColor = String(hit?.meta?.custom?.color || "").trim().toLowerCase();
            const lensColor = String(hit?.meta?.custom?.lens_color_description || "").trim().toLowerCase();

            return colorCode
                || [frameColor, lensColor].filter(Boolean).join("|")
                || String(hit?.handle || hit?.objectID || "").trim().toLowerCase();
        }

        filterUngroupedCollectionHits(hits = []) {
            if (!this.isCollectionUngrouped() || !Array.isArray(hits) || hits.length === 0) {
                return Array.isArray(hits) ? hits : [];
            }

            const filteredHits = [];

            for (const hit of hits) {
                const colorKey = this.getHitColorKey(hit);

                if (!colorKey || this.ungroupedColorKeySet.has(colorKey)) {
                    continue;
                }

                this.ungroupedColorKeySet.add(colorKey);
                filteredHits.push(hit);
            }

            return filteredHits;
        }
        
        static validateDateAnalytics(str) {
            if (!str) return '';
            
            return str
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_-]+/g, ' ')
                .trim()
                .split(' ')
                .map((word, index) => {
                if (index === 0) {
                    return word.toLowerCase();
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                })
                .join('');
        }

        isAbortError(error) {
            return error?.name === "AbortError";
        }

        getFacetCacheKey(facetFilters = [], indexName, query = null) {
            const rawFilters = JSON.stringify(facetFilters || []);
            const requestedIndex = indexName || this.getLastIndexName();
            const facetsIndex = this.getFiltersIndexName(requestedIndex);
            if (this.template === "search") {
                const queryKey = this.getSearchQueryKey(query ?? this.getCurrentSearchQuery());
                return `${facetsIndex}::${queryKey || "[[no-query]]"}::${rawFilters}`;
            }
            return `${facetsIndex}::${rawFilters}`;
        }

        setCachedFacetResults(cacheKey, results) {
            return setBoundedMapValue(this.filteredFacetsMap, cacheKey, results, FACET_RESULTS_CACHE_LIMIT);
        }

        setCachedResultCount(cacheKey, count) {
            return setBoundedMapValue(this.filteredResultCountMap, cacheKey, count, RESULT_COUNT_CACHE_LIMIT);
        }

        getCachedPlpGroupsByKey() {
            return window.__poLastPLPGroupsByKey instanceof Map
                ? new Map(window.__poLastPLPGroupsByKey)
                : new Map();
        }

        getCachedPlpFullHitsByHandle() {
            return window.__poLastPLPFullHitsByHandle instanceof Map
                ? new Map(window.__poLastPLPFullHitsByHandle)
                : new Map();
        }

        cachePlpFullHits(hits = []) {
            const hitsByHandle = this.getCachedPlpFullHitsByHandle();

            hits.forEach((hit) => {
                if (!hit?.handle) {
                    return;
                }

                hitsByHandle.set(hit.handle, hit);
            });

            window.__poLastPLPFullHitsByHandle = hitsByHandle;
            return hitsByHandle;
        }

        setCachedPlpGroups(groups = []) {
            const groupsByKey = this.getCachedPlpGroupsByKey();

            groups.forEach((group) => {
                if (!group?.xGroupKey) {
                    return;
                }

                groupsByKey.set(group.xGroupKey, Array.isArray(group.hits) ? group.hits : []);
            });

            window.__poLastPLPGroupsByKey = groupsByKey;

            if (groupsByKey.size > 0) {
                this.schedulePersistCachedPlpGroups(groupsByKey);
            }

            return Array.from(groupsByKey.entries()).map(([xGroupKey, hits]) => ({
                xGroupKey,
                hits,
            }));
        }

        getGroupsByKey(groups = []) {
            if (groups instanceof Map) {
                return groups;
            }

            const groupsByKey = new Map();

            (Array.isArray(groups) ? groups : []).forEach((group) => {
                if (!group?.xGroupKey) {
                    return;
                }

                groupsByKey.set(group.xGroupKey, Array.isArray(group.hits) ? group.hits : []);
            });

            return groupsByKey;
        }

        getOrderedGroupHandles(groupKey, fallbackHandle = "", groupsByKey = this.getCachedPlpGroupsByKey()) {
            const groupHits = groupKey && groupsByKey instanceof Map
                ? groupsByKey.get(groupKey)
                : null;
            const orderedHandles = [];
            const seenHandles = new Set();
            const appendHandle = (handle) => {
                const normalizedHandle = String(handle || "").trim();
                const dedupeKey = normalizedHandle.toLowerCase();

                if (!normalizedHandle || seenHandles.has(dedupeKey)) {
                    return;
                }

                orderedHandles.push(normalizedHandle);
                seenHandles.add(dedupeKey);
            };

            if (Array.isArray(groupHits)) {
                groupHits.forEach((hit) => appendHandle(hit?.handle));
            }

            // Group rules take precedence over the representative from the main PLP query.
            appendHandle(fallbackHandle);

            return orderedHandles;
        }

        getRepresentativeGroupHandle(groupKey, _indexName = this.getLastIndexName(), fallbackHandle = "") {
            return this.getOrderedGroupHandles(groupKey, fallbackHandle)[0] || "";
        }

        setProductCardUrlHandle(productCardUrl, handle) {
            const normalizedHandle = String(handle || "").trim();
            if (!productCardUrl?.searchParams || !normalizedHandle) {
                return productCardUrl;
            }

            if (productCardUrl.searchParams.has("product_handle")) {
                productCardUrl.searchParams.set("product_handle", normalizedHandle);
                return productCardUrl;
            }

            const routesRoot = String(Shopify.routes?.root || "/");
            const normalizedRoot = routesRoot.endsWith("/") ? routesRoot : `${routesRoot}/`;
            productCardUrl.pathname = `${normalizedRoot}products/${encodeURIComponent(normalizedHandle)}`.replace(/\/{2,}/g, "/");

            return productCardUrl;
        }

        applyGroupSelectionToProductCardUrl(productCardUrl, groupKey, indexName = this.getLastIndexName(), preferredHandle = "") {
            if (!productCardUrl?.searchParams || !groupKey) {
                return productCardUrl;
            }

            const groupOrder = this.getOrderedGroupHandles(groupKey, preferredHandle).join(",");
            if (groupOrder) {
                productCardUrl.searchParams.set("po_group_order", groupOrder);
            }

            const representativeHandle = this.getRepresentativeGroupHandle(groupKey, indexName, preferredHandle);
            if (representativeHandle) {
                this.setProductCardUrlHandle(productCardUrl, representativeHandle);
            }

            return productCardUrl;
        }

        resolveProductCardFetchUrl(productCardFetchUrl, groupKey, indexName = this.getLastIndexName(), preferredHandle = "") {
            if (!productCardFetchUrl || !groupKey) {
                return productCardFetchUrl;
            }

            try {
                const url = new URL(productCardFetchUrl, window.location.origin);
                const hadGroupOrder = url.searchParams.has("po_group_order");
                const originalPathname = url.pathname;
                const originalProductHandle = url.searchParams.get("product_handle");
                this.applyGroupSelectionToProductCardUrl(url, groupKey, indexName, preferredHandle);
                return (
                    hadGroupOrder
                    || url.searchParams.has("po_group_order")
                    || url.pathname !== originalPathname
                    || url.searchParams.get("product_handle") !== originalProductHandle
                )
                    ? url.toString()
                    : productCardFetchUrl;
            } catch (_e) {
                return productCardFetchUrl;
            }
        }

        reorderElementsByVariantHandle(container, selector, handleAttribute, orderMap) {
            if (!container || !(orderMap instanceof Map) || orderMap.size === 0) {
                return false;
            }

            const items = Array.from(container.querySelectorAll(selector));
            if (items.length < 2) {
                return false;
            }

            const orderedItems = items
                .map((element, originalIndex) => {
                    const handle = String(element.dataset?.[handleAttribute] || "").trim();
                    const normalizedHandle = handle.toLowerCase();
                    return {
                        element,
                        originalIndex,
                        order: orderMap.has(handle)
                            ? orderMap.get(handle)
                            : orderMap.has(normalizedHandle)
                                ? orderMap.get(normalizedHandle)
                                : Number.MAX_SAFE_INTEGER,
                    };
                })
                .sort((left, right) => {
                    if (left.order !== right.order) {
                        return left.order - right.order;
                    }

                    return left.originalIndex - right.originalIndex;
                });

            const hasOrderChanged = orderedItems.some(
                (entry, index) => entry.element !== items[index]
            );

            if (!hasOrderChanged) {
                return false;
            }

            orderedItems.forEach(({ element }) => container.appendChild(element));
            return true;
        }

        /**
         * Resolves PLP OOS copy from an Algolia hit.
         * @param {object|null|undefined} hit
         * @returns {string}
         */
        resolvePlpStatusFromHit(hit) {
            if (!hit || hit.inventory_available !== false) {
                return "";
            }

            const disco = hit?.meta?.custom?.disco?.value ?? hit?.meta?.custom?.disco ?? false;
            const isDisco = disco === true || disco === "true" || disco === 1 || disco === "1";
            const strings =  window.variantStrings || {};

            return isDisco
                ? (strings.soldOut || "SOLD OUT")
                : (strings.comingBackSoon || "COMING BACK SOON");
        }

        /**
         * Finds a cached Algolia hit for a product handle.
         * @param {string} handle
         * @param {string} [groupKey]
         * @returns {object|null}
         */
        getHitForHandle(handle, groupKey = "") {
            const normalizedHandle = String(handle || "").trim();
            if (!normalizedHandle) {
                return null;
            }

            const fullHit = this.getCachedPlpFullHitsByHandle().get(normalizedHandle);
            if (fullHit) {
                return fullHit;
            }

            if (groupKey) {
                return this.getResolvedGroupHits(groupKey).find((hit) => hit?.handle === normalizedHandle) || null;
            }

            return null;
        }

        /**
         * Syncs OOS labels on PLP thumbnails from Algolia inventory data.
         * @param {Element|null} card
         * @param {Map<string, object[]>} [groupsByKey]
         */
        syncPlpThumbnailStatusForCard(card, groupsByKey = this.getCachedPlpGroupsByKey()) {
            if (!card) {
                return;
            }

            const groupKey = card.dataset?.groupKey || "";
            const sliderComponent = card.querySelector("po-slider-component");
            const PoSlider = window.PoSliderComponent;
            const thumbs = Array.from(card.querySelectorAll("[data-current-variant]"));

            if (thumbs.length > 0) {
                thumbs.forEach((thumb) => {
                    if (thumb.hasAttribute("data-plp-status")) {
                        thumb.dataset.plpStatus = (thumb.getAttribute("data-plp-status") || "").trim();
                        return;
                    }

                    const handle = thumb.dataset?.currentVariant || "";
                    const hit = this.getHitForHandle(handle, groupKey);
                    thumb.dataset.plpStatus = this.resolvePlpStatusFromHit(hit);
                });
            } else {
                const handle = card.dataset?.laDnProductHandle || "";
                const hit = this.getHitForHandle(handle, groupKey);
                const status = this.resolvePlpStatusFromHit(hit);

                if (PoSlider?.applyPlpProdStatus) {
                    PoSlider.applyPlpProdStatus(card, status);
                }

                return;
            }

            const selectedThumb = card.querySelector("[data-current-variant][data-selected='true']")
                || thumbs[0];

            if (!selectedThumb) {
                return;
            }

            const status = selectedThumb.dataset?.plpStatus?.trim() ?? "";

            if (PoSlider?.applyPlpProdStatus) {
                PoSlider.applyPlpProdStatus(card, status);
            }

            if (sliderComponent?.bootstrapPlpInfo) {
                sliderComponent.lastAppliedVariantId = null;
                sliderComponent.bootstrapPlpInfo(selectedThumb);
            }
        }

        syncRenderedProductCardVariantOrderForCard(card, groupsByKey = this.getCachedPlpGroupsByKey()) {
            if (!card || card.querySelector("po-plp-product-card")) {
                return false;
            }

            const groupKey = card.dataset?.groupKey;
            const preferredHandle = card.dataset?.algoliaHitHandle || "";
            const orderedHandles = this.getOrderedGroupHandles(groupKey, preferredHandle, groupsByKey);

            if (orderedHandles.length < 1) {
                this.syncPlpThumbnailStatusForCard(card, groupsByKey);
                return false;
            }

            const orderMap = new Map(orderedHandles.flatMap((handle, index) => {
                const normalizedHandle = String(handle || "").trim();
                return normalizedHandle
                    ? [[normalizedHandle, index], [normalizedHandle.toLowerCase(), index]]
                    : [];
            }));
            const sliderComponent = card.querySelector("po-slider-component");
            const slideContainer = sliderComponent?.querySelector('ul[id^="Slider-"]');
            const thumbnailContainer = sliderComponent?.querySelector(".po-product-card-thumbnails");
            const slidesChanged = this.reorderElementsByVariantHandle(
                slideContainer,
                "[data-variant-handle]",
                "variantHandle",
                orderMap
            );
            const thumbsChanged = this.reorderElementsByVariantHandle(
                thumbnailContainer,
                "[data-current-variant]",
                "currentVariant",
                orderMap
            );

            if (!slidesChanged && !thumbsChanged) {
                if (card.isConnected) {
                    this.syncPlpThumbnailStatusForCard(card, groupsByKey);
                }
                return false;
            }

            const firstThumb = thumbnailContainer?.querySelector("[data-current-variant]");
            const firstSlide = slideContainer?.querySelector("[data-variant-handle]");
            const activeThumb = firstThumb;
            const activeSlide = firstSlide;
            const normalizedThumbIndex = 0;
            const activeHandle = activeThumb?.dataset?.currentVariant || activeSlide?.dataset?.variantHandle || "";
            const cardHandle = card.dataset?.plpProductHandle || card.dataset?.laDnProductHandle || "";
            const activePriceCacheKey = activeHandle ? `${activeThumb?.dataset?.href || activeHandle}-dinamic-price` : "";
            const hasActivePriceCache = Boolean(
                activePriceCacheKey && window.PoSliderComponent?.responseCache?.has?.(activePriceCacheKey)
            );

            card.toggleAttribute(
                "data-plp-price-sync-pending",
                Boolean(activeHandle && cardHandle && activeHandle !== cardHandle && !hasActivePriceCache)
            );

            thumbnailContainer?.querySelectorAll("[data-current-variant]").forEach((thumb, index) => {
                thumb.dataset.selected = index === normalizedThumbIndex ? "true" : "false";
            });

            const firstHref = activeThumb?.dataset?.href || activeSlide?.querySelector("a[href]")?.href || "";
            const productAnchor = card.querySelector("[data-product-href], .tile-title a");
            if (productAnchor && firstHref) {
                productAnchor.href = firstHref;
            }

            sliderComponent.thumbnailItems = sliderComponent.querySelectorAll('[id^="Thumbnail-"]');
            sliderComponent.sliderItems = sliderComponent.querySelectorAll('[id^="Slide-"]');
            sliderComponent.currentPage = normalizedThumbIndex + 1;
            sliderComponent.currentHandle = activeHandle;
            sliderComponent.lastAppliedVariantId = null;
            if (sliderComponent.slider) {
                sliderComponent.slider.scrollLeft = 0;
            }

            sliderComponent.querySelectorAll(".slider-counter--current, .slider-counter-current--mobile").forEach((counter) => {
                counter.textContent = String(normalizedThumbIndex + 1);
            });

            if (typeof sliderComponent.resetPages === "function") {
                sliderComponent.resetPages();
            }
            if (typeof sliderComponent.setSlidePosition === "function") {
                sliderComponent.setSlidePosition(0, "auto");
            } else if (sliderComponent.slider) {
                sliderComponent.slider.scrollLeft = 0;
            }

            if (activeThumb && typeof sliderComponent.syncPlpHitSelectionWithoutScroll === "function") {
                sliderComponent.syncPlpHitSelectionWithoutScroll();
            } else if (activeThumb && typeof sliderComponent.changePlpInfo === "function") {
                sliderComponent.changePlpInfo(activeThumb);
            }

            if (card.isConnected) {
                this.syncPlpThumbnailStatusForCard(card, groupsByKey);
            }

            poPlpSortDebug("syncRenderedProductCardVariantOrderForCard", {
                groupKey,
                orderedHandles,
                renderedThumbs: Array.from(thumbnailContainer?.querySelectorAll("[data-current-variant]") || [])
                    .map((thumb) => thumb.dataset.currentVariant),
            });

            return true;
        }

        syncRenderedProductCardVariantOrder(groups = null) {
            const groupsByKey = groups ? this.getGroupsByKey(groups) : this.getCachedPlpGroupsByKey();

            document
                .querySelectorAll(".po-algolia-product-card[data-group-key]")
                .forEach((card) => this.syncRenderedProductCardVariantOrderForCard(card, groupsByKey));
        }

        prioritizeProductCardVariant(card, desiredHandle) {
            const normalizedDesiredHandle = String(desiredHandle || "").trim();
            if (!card || !normalizedDesiredHandle || card.querySelector("po-plp-product-card")) {
                return false;
            }

            const orderMap = new Map([
                [normalizedDesiredHandle, 0],
                [normalizedDesiredHandle.toLowerCase(), 0],
            ]);
            const sliderComponent = card.querySelector("po-slider-component");
            const slideContainer = sliderComponent?.querySelector('ul[id^="Slider-"]');
            const thumbnailContainer = sliderComponent?.querySelector(".po-product-card-thumbnails");
            const slidesChanged = this.reorderElementsByVariantHandle(
                slideContainer,
                "[data-variant-handle]",
                "variantHandle",
                orderMap
            );
            const thumbsChanged = this.reorderElementsByVariantHandle(
                thumbnailContainer,
                "[data-current-variant]",
                "currentVariant",
                orderMap
            );
            const firstThumb = thumbnailContainer?.querySelector("[data-current-variant]");
            const firstSlide = slideContainer?.querySelector("[data-variant-handle]");
            const firstHandle = firstThumb?.dataset?.currentVariant || firstSlide?.dataset?.variantHandle || "";

            if (String(firstHandle || "").trim().toLowerCase() !== normalizedDesiredHandle.toLowerCase()) {
                return false;
            }

            thumbnailContainer?.querySelectorAll("[data-current-variant]").forEach((thumb, index) => {
                thumb.dataset.selected = index === 0 ? "true" : "false";
            });

            const firstHref = firstThumb?.dataset?.href || firstSlide?.querySelector("a[href]")?.href || "";
            const productAnchor = card.querySelector("[data-product-href], .tile-title a");
            if (productAnchor && firstHref) {
                productAnchor.href = firstHref;
            }

            return slidesChanged || thumbsChanged;
        }

        syncRenderedProductCardHandles(groups = null, indexName = this.getLastIndexName()) {
            if (this.template !== "collection" || this.isCollectionUngrouped()) {
                return;
            }

            const groupsByKey = groups ? this.getGroupsByKey(groups) : this.getCachedPlpGroupsByKey();

            document.querySelectorAll(".ais-Hits-list > .ais-Hits-item").forEach((item) => {
                const groupKey = this.getRenderedGroupKeyForItem(item);
                const groupHits = groupKey ? groupsByKey.get(groupKey) : null;
                if (!Array.isArray(groupHits) || groupHits.length === 0) {
                    return;
                }

                const slot = item.querySelector(".po-card-fetch-slot");
                const pendingMeta = this.pendingCardSlots.get(slot?.id) || {};
                const slotMeta = this.getCardSlotMeta(slot) || {};
                const preferredHandle = String(
                    slotMeta.algoliaHitHandle ||
                    pendingMeta.algoliaHitHandle ||
                    ""
                ).trim();
                const representativeHandle = this.getRepresentativeGroupHandle(groupKey, indexName, preferredHandle);
                if (!representativeHandle || !slot?.id) {
                    return;
                }

                const fetchUrl = this.resolveProductCardFetchUrl(
                    slotMeta.fetchUrl || pendingMeta.fetchUrl || "",
                    groupKey,
                    indexName,
                    preferredHandle || representativeHandle
                );

                if (!fetchUrl) {
                    return;
                }

                const nextMeta = {
                    handle: representativeHandle,
                    algoliaHitHandle: preferredHandle || representativeHandle,
                    groupKey,
                    fetchUrl,
                };

                if (this.pendingCardSlots.has(slot.id)) {
                    this.pendingCardSlots.set(slot.id, {
                        ...pendingMeta,
                        ...nextMeta,
                    });
                }

                this.pendingHydrationQueue = this.pendingHydrationQueue.map((task) => (
                    task?.slotId === slot.id
                        ? {
                            ...task,
                            handle: representativeHandle,
                            fetchUrl,
                        }
                        : task
                ));

                this.updateCardSlotMeta(slot, nextMeta);

                if (slot.dataset.cardFetchState !== "loaded" && slot.dataset.cardFetchState !== "loading") {
                    return;
                }

                const currentHandle = this.getRenderedSearchHandleForItem(item);
                if (String(currentHandle || "").trim().toLowerCase() === representativeHandle.toLowerCase()) {
                    return;
                }

                const controller = new AbortController();
                this.pendingCardControllers.add(controller);
                slot.dataset.cardFetchState = "loading";
                this.fetchProductCard(
                    representativeHandle,
                    fetchUrl,
                    slot.id,
                    slotMeta.trackSearchAttribution || pendingMeta.trackSearchAttribution || null,
                    slotMeta.index ?? pendingMeta.index,
                    slotMeta.renderToken || pendingMeta.renderToken || this.productRenderToken,
                    controller,
                    { countProduct: false }
                );
            });
        }

        schedulePersistCachedPlpGroups(groupsByKey = new Map()) {
            clearTimeout(this.persistGroupsTimer);
            this.persistGroupsTimer = window.setTimeout(() => {
                try {
                    const serializedGroups = Array.from(groupsByKey.entries()).map(([xGroupKey, hits]) => ({
                        xGroupKey,
                        hits,
                    }));
                    sessionStorage.setItem("lastPLPGroups", JSON.stringify(serializedGroups));
                } catch (_error) {
                    // no-op: sessionStorage can be unavailable in privacy mode
                }
            }, 120);
        }

        clearCachedPlpGroups() {
            window.__poLastPLPGroupsByKey = new Map();
            window.__poLastPLPFullHitsByHandle = new Map();
            clearTimeout(this.persistGroupsTimer);

            try {
                sessionStorage.setItem("lastPLPGroups", "[]");
            } catch (_error) {
                // no-op: sessionStorage can be unavailable in privacy mode
            }
        }

        abortPendingCardFetches() {
            this.pendingCardControllers.forEach((controller) => controller.abort());
            this.pendingCardControllers.clear();
        }

        abortPendingGroupFetch() {
            this.pendingGroupController?.abort();
            this.pendingGroupController = null;
        }

        abortPendingFacetFetch() {
            this.pendingFacetController?.abort();
            this.pendingFacetController = null;
        }

        abortPendingResultCountFetch() {
            this.pendingResultCountController?.abort();
            this.pendingResultCountController = null;
        }

        prepareProductRender() {
            this.productRenderToken += 1;
            this.abortPendingCardFetches();
            this.abortPendingGroupFetch();
            this.abortPendingResultCountFetch();
            this.firstPageHits = [];
            this.cardPromises.length = 0;
            this.groupKeys.length = 0;
            this.groupKeySet.clear();
            this.ungroupedColorKeySet.clear();
            this.productsAdded = 0;
            this.searchLoadMoreOffset = 0;
            this.searchLoadMoreExhausted = false;
            this.pendingCardSlots.clear();
            this.pendingHydrationQueue.length = 0;
            this.cardSlotMetaMap.clear();
            this.virtualCardObserver?.disconnect();
            this.clearCachedPlpGroups();

            return this.productRenderToken;
        }

        getCachedCardMarkup(cacheKey) {
            if (!cacheKey || !this.cardMarkupCache.has(cacheKey)) {
                return "";
            }

            const markup = this.cardMarkupCache.get(cacheKey) || "";
            this.cardMarkupCache.delete(cacheKey);
            this.cardMarkupCache.set(cacheKey, markup);
            return markup;
        }

        appendPoDkCodesToUrl(url) {
            if (!url?.searchParams) {
                return;
            }

            const readCookieCodes = (name) => {
                const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
                return match ? decodeURIComponent(match[1]) : "";
            };

            const csv =
                document.documentElement?.dataset?.poDkAppliedCodes ||
                (typeof window.PoDkStorefrontPrices?.getAppliedCodesCsv === "function"
                    ? window.PoDkStorefrontPrices.getAppliedCodesCsv()
                    : "") ||
                "";

            const codes = new Set(
                csv
                    .split(",")
                    .map((code) => code.trim().toLowerCase())
                    .filter(Boolean)
            );

            ["discount_code", "discount", "po_dk_active_codes"].forEach((cookieName) => {
                readCookieCodes(cookieName)
                    .split(",")
                    .map((code) => code.trim().toLowerCase())
                    .filter(Boolean)
                    .forEach((code) => codes.add(code));
            });

            try {
                const params = new URLSearchParams(window.location.search);
                if (params.get("discount")) {
                    params.get("discount").split(",").forEach((code) => {
                        const normalized = code.trim().toLowerCase();
                        if (normalized) codes.add(normalized);
                    });
                }
            } catch (_error) {
                // no-op
            }

            if (codes.size) {
                url.searchParams.set("po_dk_codes", Array.from(codes).join(","));
            }
        }

        clearCardMarkupCache() {
            this.cardMarkupCache?.clear();
        }

        setCachedCardMarkup(cacheKey, markup) {
            if (!cacheKey || !markup) {
                return markup;
            }

            if (this.cardMarkupCache.has(cacheKey)) {
                this.cardMarkupCache.delete(cacheKey);
            }

            this.cardMarkupCache.set(cacheKey, markup);

            while (this.cardMarkupCache.size > this.cardMarkupCacheLimit) {
                const oldestKey = this.cardMarkupCache.keys().next().value;
                if (oldestKey === undefined) break;
                this.cardMarkupCache.delete(oldestKey);
            }

            return markup;
        }

        shouldWindowProductCards() {
            return false;
        }

        initVirtualCardObserver() {
            if (this.virtualCardObserver || !this.shouldWindowProductCards()) {
                return;
            }

            this._remountQueue = [];
            this._remountScheduled = false;

            this.virtualCardObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const slot = entry.target;
                        if (!(slot instanceof HTMLElement) || !slot.id) return;

                        if (entry.isIntersecting) {
                            this._remountQueue.push(slot);
                            this._scheduleRemountFlush();
                            return;
                        }

                        this.virtualizeCardSlot(slot);
                    });
                },
                { rootMargin: this.virtualCardObserverRootMargin, threshold: 0.01 }
            );
        }

        _scheduleRemountFlush() {
            if (this._remountScheduled) return;
            this._remountScheduled = true;

            requestAnimationFrame(() => {
                this._flushRemountQueue();
            });
        }


        _flushRemountQueue() {
            const BATCH_SIZE = 4; // monta N card per frame
            const batch = this._remountQueue.splice(0, BATCH_SIZE);

            batch.forEach((slot) => this.remountVirtualizedCardSlot(slot));

            if (this._remountQueue.length > 0) {
                requestAnimationFrame(() => this._flushRemountQueue());
            } else {
                this._remountScheduled = false;
            }
        }

        observeVirtualCardSlot(slot) {
            if (!this.virtualCardObserver || !slot?.id) {
                return;
            }

            this.virtualCardObserver.observe(slot);
        }

        getCardSlotMeta(slotOrId) {
            const slotId = typeof slotOrId === "string" ? slotOrId : slotOrId?.id;
            if (!slotId) {
                return null;
            }

            return this.cardSlotMetaMap.get(slotId) || null;
        }

        updateCardSlotMeta(slotOrId, partialMeta = {}) {
            const slotId = typeof slotOrId === "string" ? slotOrId : slotOrId?.id;
            if (!slotId) {
                return null;
            }

            const nextMeta = {
                ...(this.cardSlotMetaMap.get(slotId) || {}),
                ...partialMeta,
            };

            this.cardSlotMetaMap.set(slotId, nextMeta);
            return nextMeta;
        }

        captureCardSlotState(slot) {
            if (!slot?.id) {
                return null;
            }

            const meta = this.getCardSlotMeta(slot) || {};
            const cardElement = slot.querySelector(".po-algolia-product-card");
            const sliderComponent = cardElement?.querySelector?.("po-slider-component");
            const slider = sliderComponent?.slider || sliderComponent?.querySelector?.('[id^="Slider-"]');
            const thumbnailWrapper = sliderComponent?.thumbnailWrapper || sliderComponent?.querySelector?.(".slider-thumbnails");
            const selectedThumbnail = sliderComponent?.thumbnailWrapper?.querySelector?.("[data-selected=true]")
                || sliderComponent?.querySelector?.(".slider-thumbnails [data-selected=true]")
                || cardElement?.querySelector?.(".slider-thumbnails [data-selected=true]");
            const currentVariantHandle = selectedThumbnail?.dataset?.currentVariant
                || sliderComponent?.currentHandle
                || meta.currentVariantHandle
                || null;
            const currentMarkup = slot.innerHTML || meta.currentMarkup || "";
            const measuredHeight = Math.max(
                slot.offsetHeight || 0,
                cardElement?.offsetHeight || 0,
                meta.measuredHeight || 0
            );

            return this.updateCardSlotMeta(slot, {
                currentMarkup,
                measuredHeight,
                currentVariantHandle,
                sliderScrollLeft: slider ? slider.scrollLeft : meta.sliderScrollLeft || 0,
                thumbnailScrollLeft: thumbnailWrapper ? thumbnailWrapper.scrollLeft : meta.thumbnailScrollLeft || 0,
                isVirtualized: false,
            });
        }

        virtualizeCardSlot(slot) {
            if (!this.shouldWindowProductCards() || !slot?.id) return;
            if (slot.dataset.cardFetchState !== "loaded" || slot.dataset.cardVirtualized === "true") return;

            const cardElement = slot.querySelector(".po-algolia-product-card");
            if (!cardElement) return;

            const meta = this.captureCardSlotState(slot);
            const measuredHeight = Math.max(meta?.measuredHeight || 0, slot.offsetHeight || 0);
            if (!measuredHeight) return;

            // ✅ salva markup nel meta PRIMA di svuotare innerHTML
            this.updateCardSlotMeta(slot, {
                currentMarkup: slot.innerHTML,
                measuredHeight,
                isVirtualized: true,
            });

            slot.style.minHeight = `${Math.ceil(measuredHeight)}px`;
            slot.dataset.cardVirtualized = "true";
            slot.innerHTML = '<div class="po-card-window-placeholder" aria-hidden="true"></div>';
        }

        restoreVirtualizedSliderState(slot, meta = {}) {
            const sliderComponent = slot.querySelector("po-slider-component");
            if (!sliderComponent) {
                return;
            }

            const restoreSelectedVariant = () => {
                const sliderItems = Array.from(sliderComponent.thumbnailItems || []);
                const targetIndex = meta.currentVariantHandle
                    ? sliderItems.findIndex((item) => item?.dataset?.currentVariant === meta.currentVariantHandle)
                    : -1;

                if (targetIndex < 0) {
                    return false;
                }

                sliderComponent.thumbnailItems?.forEach?.((item) => {
                    item.dataset.selected = "false";
                });
                sliderComponent.updateSelectedThumbnail?.(targetIndex);
                const slidePosition = sliderComponent.getSlidePositionByIndex?.(targetIndex);
                if (Number.isFinite(slidePosition)) {
                    sliderComponent.setSlidePosition?.(slidePosition, "auto");
                }
                sliderComponent.update?.();
                return true;
            };

            requestAnimationFrame(() => {
                const restoredByVariant = restoreSelectedVariant();

                if (!restoredByVariant) {
                    if (sliderComponent.slider && Number.isFinite(meta.sliderScrollLeft)) {
                        sliderComponent.slider.scrollLeft = meta.sliderScrollLeft;
                    }

                    const thumbnailWrapper = sliderComponent.thumbnailWrapper
                        || sliderComponent.querySelector(".slider-thumbnails");

                    if (thumbnailWrapper && Number.isFinite(meta.thumbnailScrollLeft)) {
                        thumbnailWrapper.scrollLeft = meta.thumbnailScrollLeft;
                    }

                    sliderComponent.update?.();
                }

                requestAnimationFrame(() => {
                    slot.style.removeProperty("min-height");
                });
            });
        }

        remountVirtualizedCardSlot(slot) {
            if (!slot?.id || slot.dataset.cardVirtualized !== "true") return;

            const meta = this.getCardSlotMeta(slot);
            const restoredMarkup = meta?.currentMarkup;
            
            if (!restoredMarkup) return;

            slot.innerHTML = restoredMarkup;
            slot.dataset.cardVirtualized = "false";

            const updatedMeta = this.captureCardSlotState(slot);
            this.restoreVirtualizedSliderState(slot, updatedMeta || meta || {});

            const restoredCard = slot.querySelector(".po-algolia-product-card");
            document.dispatchEvent(new CustomEvent("plp:card-rendered", {
                detail: { card: restoredCard }
            }));
        }

        initCardSlotObserver() {
            if (this.cardSlotObserver) {
                return;
            }

            if (typeof IntersectionObserver !== "function") {
                return;
            }

            this.cardSlotObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.cardSlotObserver.unobserve(entry.target);
                        this.hydrateCardSlot(entry.target);
                    });
                },
                { rootMargin: this.cardSlotObserverRootMargin }
            );
        }

        observePendingCardSlots(root = document) {
            if (!this.cardSlotObserver || !root?.querySelectorAll) {
                return;
            }

            if (root.matches?.(".po-card-fetch-slot")) {
                this.observeCardSlot(root);
            }

            root.querySelectorAll(".po-card-fetch-slot").forEach((slot) => {
                this.observeCardSlot(slot);
            });
        }

        observeCardSlot(slot) {
            if (!slot?.id || !this.pendingCardSlots.has(slot.id)) {
                return;
            }

            if (slot.dataset.cardFetchState && slot.dataset.cardFetchState !== "idle") {
                return;
            }

            if (!this.cardSlotObserver) {
                this.hydrateCardSlot(slot);
                return;
            }

            this.cardSlotObserver.observe(slot);
        }

        queueObserveCardSlot(slotId, attempt = 0) {
            if (!slotId) {
                return;
            }

            requestAnimationFrame(() => {
                const slot = document.getElementById(slotId);

                if (slot) {
                    this.observeCardSlot(slot);
                    return;
                }

                if (attempt < 4 && this.pendingCardSlots.has(slotId)) {
                    this.queueObserveCardSlot(slotId, attempt + 1);
                }
            });
        }

        hydrateCardSlot(slot) {
            if (!slot?.id) {
                return;
            }

            const meta = this.pendingCardSlots.get(slot.id);
            if (!meta || meta.renderToken !== this.productRenderToken) {
                this.pendingCardSlots.delete(slot.id);
                return;
            }

            slot.dataset.cardFetchState = "queued";
            this.pendingHydrationQueue.push({
                slotId: slot.id,
                handle: meta.handle,
                fetchUrl: meta.fetchUrl,
                trackSearchAttribution: meta.trackSearchAttribution,
                index: meta.index,
                renderToken: meta.renderToken,
            });
            this.flushPendingCardHydrations();
        }

        flushPendingCardHydrations() {
            while (
                this.activeCardHydrations < this.maxConcurrentCardHydrations &&
                this.pendingHydrationQueue.length > 0
            ) {
                const task = this.pendingHydrationQueue.shift();
                if (!task?.slotId) {
                    continue;
                }

                const slot = document.getElementById(task.slotId);
                const meta = this.pendingCardSlots.get(task.slotId);
                if (!slot || !meta || meta.renderToken !== task.renderToken || task.renderToken !== this.productRenderToken) {
                    this.pendingCardSlots.delete(task.slotId);
                    continue;
                }

                slot.dataset.cardFetchState = "loading";
                this.activeCardHydrations += 1;

                const controller = new AbortController();
                this.pendingCardControllers.add(controller);

                this.fetchProductCard(
                    task.handle,
                    task.fetchUrl,
                    task.slotId,
                    task.trackSearchAttribution,
                    task.index,
                    task.renderToken,
                    controller
                ).finally(() => {
                    this.pendingCardSlots.delete(task.slotId);
                    this.activeCardHydrations = Math.max(0, this.activeCardHydrations - 1);
                    this.flushPendingCardHydrations();
                });
            }
        }

        addGroupKey(groupKey) {
            if (this.isCollectionUngrouped() || !groupKey || this.groupKeySet.has(groupKey)) {
                return;
            }

            this.groupKeySet.add(groupKey);
            this.groupKeys.push(groupKey);
        }


        getCurrentPage() {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            const value = params.get('page');
            return Number(value);
        }

        waitForElement(selector, callback) {
            const target = document.querySelector("#MainContent") || document.body;
            const observer = new MutationObserver((_mutations, obs) => {
                if (document.querySelector(selector)) {
                    try {
                        callback.call(this);
                    } catch (e) {
                        console.error(e);
                    }
                    obs.disconnect();
                }
            });
            observer.observe(target, {childList: true, subtree: true});
        }

        getSort() {
            const template = document.querySelector("#po-algolia-sort-chip");
            this.originalSort = document.querySelector(".po-original-sort .ais-SortBy-select");
            const sortModalWrapper = document.querySelector(".po-sort-modal-wrapper");
            const rawSortOrders = filterSortOrdersByAllowlist(
                Array.isArray(window.algoliaShopify?.sortOrders)
                    ? window.algoliaShopify.sortOrders
                    : this.sortOrders
            );
            if (!this.originalSort || !template || !rawSortOrders || !sortModalWrapper) return;

            const activeCurrency = this.getActiveCurrency();
            const activeLower = activeCurrency ? activeCurrency.toLowerCase() : "";
            const sortOrders = activeLower
                ? rawSortOrders.filter(({ value = "" }) => {
                      const normalized = String(value).toLowerCase();
                      return !normalized.includes("pricing_") || normalized.includes(`_${activeLower}`);
                  })
                : rawSortOrders;

            sortModalWrapper.innerHTML = "";

            sortOrders.forEach((sort, index) => {
                const clone = template.content.cloneNode(true);
                let originalOption = clone.querySelector('label');
                const div = document.createElement("div");
                div.innerHTML = originalOption.outerHTML
                    .replaceAll("[id]", sort.value)
                    .replaceAll("[name]", "po-sort-chip")
                    .replaceAll("[label]", sort.label);
                const radio = div.querySelector("input[type='radio']");
                radio.checked = index === 0;
                radio.addEventListener("change", () => {
                    this.originalSort.value = sort.value;
                    this.originalSort.dispatchEvent(new Event("change"));
                });
                sortModalWrapper.append(div);
            });
        }

        getDesktopColumns() {
            return this.desktopColumns || Number(document.currentScript?.dataset?.desktopColumns) || 1;
        }

        getMobileColumns() {
            const layoutOne = document.getElementById("layout-1col");
            const layoutTwo = document.getElementById("layout-2col");

            if (layoutOne?.checked) return 1;
            if (layoutTwo?.checked) return 2;
            return 2;
        }

        getProductsPerPage() {
            const isDesktop = typeof window.matchMedia === "function"
                ? window.matchMedia("(min-width: 1024px)").matches
                : true;
            const configuredProductsPerPage = !isDesktop && this.template === "collection"
                ? this.mobileProductsPerPage
                : this.desktopProductsPerPage;
            const resolvedProductsPerPage = Math.max(
                1,
                Number(configuredProductsPerPage) || Number(this.desktopProductsPerPage) || 1
            );
            this.productsPerPage = resolvedProductsPerPage;
            return resolvedProductsPerPage;
        }

        getRowsPerPage(columns) {
            const safeColumns = Math.max(1, Number(columns) || 1);
            return Math.ceil(this.getProductsPerPage() / safeColumns);
        }

        getRowOffset(pageNumber, columns) {
            const page = Math.max(1, Number(pageNumber) || 1);
            return (page - 1) * this.getRowsPerPage(columns);
        }

        getRenderedProductCount() {
            return Array.from(
                document.querySelectorAll(".ais-Hits-list .ais-Hits-item")
            ).filter((item) =>
                !item.hidden
                && item.dataset.searchDuplicate !== "true"
                && item.dataset.ungroupedDuplicate !== "true"
            ).length;
        }

        getCardAppendBatchSize() {
            if (this.template !== "collection") {
                return 12;
            }

            const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
            const columns = isDesktop ? this.getDesktopColumns() : this.getMobileColumns();
            return Math.max(4, Math.min(12, columns * 2));
        }

        waitForNextPaint() {
            return new Promise((resolve) => {
                if (document.visibilityState === "hidden" || typeof requestAnimationFrame !== "function") {
                    window.setTimeout(resolve, 0);
                    return;
                }

                requestAnimationFrame(() => resolve());
            });
        }

        async appendProductHitsInBatches(hitsListEl, hits = [], options = {}) {
            if (!hitsListEl || !Array.isArray(hits) || hits.length === 0) {
                return;
            }

            const { markLoadMoreBatch = false, batchFromIndex = 0, renderToken = this.productRenderToken } = options;
            const batchSize = this.getCardAppendBatchSize();
            const loadMoreBatchId = String(Math.max(0, Number(batchFromIndex) || 0));

            for (let index = 0; index < hits.length; index += batchSize) {
                if (renderToken !== this.productRenderToken) return;
                const batch = hits.slice(index, index + batchSize);
                const fragment = document.createDocumentFragment();
                const pendingSlotIds = [];

                batch.forEach((hit) => {
                    const groupKey = hit.meta?.custom?.x_groupkey;
                    const li = document.createElement("li");
                    li.classList.add("ais-Hits-item");
                    if (markLoadMoreBatch) {
                        li.dataset.poPlpLoadMoreBatch = loadMoreBatchId;
                    }
                    if (groupKey) {
                        li.dataset.groupKey = groupKey;
                    }
                    li.innerHTML = this.addProductCard(hit, null, { queueObservation: false });

                    const slot = li.querySelector(".po-card-fetch-slot");
                    if (slot?.id) {
                        pendingSlotIds.push(slot.id);
                    }

                    fragment.append(li);
                    this.addGroupKey(groupKey);
                });

                hitsListEl.append(fragment);
                this.dedupeRenderedSearchHits();
                this.dedupeRenderedUngroupedCollectionItems();

                pendingSlotIds.forEach((slotId) => {
                    const slot = document.getElementById(slotId);
                    if (slot) {
                        this.observeCardSlot(slot);
                    }
                });

                if (index + batchSize < hits.length) {
                    await this.waitForNextPaint();
                }
            }
        }

        updateBannerVisibility() {
            const listEl = document.querySelector(".ais-Hits-list");
            if (!listEl) return;

            const productCount = this.getDisplayProductHits();
            if (!productCount) return;

            const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
            const columns = isDesktop ? this.getDesktopColumns() : this.getMobileColumns();
            const rowsAvailable = Math.ceil(productCount / Math.max(1, Number(columns) || 1));

            listEl.querySelectorAll(".po-collection-banner").forEach((banner) => {
                if (banner.tagName.toLowerCase() === "template") return;

                const requiredRow = Number(
                    isDesktop ? banner.dataset.rowDesktopResolved : banner.dataset.rowMobileResolved
                );

                if (!Number.isFinite(requiredRow) || requiredRow <= 0) return;

                if (requiredRow <= rowsAvailable) {
                    banner.style.removeProperty("display");
                    banner.removeAttribute("data-hidden-by-row");
                } else {
                    banner.style.display = "none";
                    banner.setAttribute("data-hidden-by-row", "true");
                }
            });
        }

        initBannerVideoController(banner) {
            const toggleBtn = banner.querySelector('[data-banner-video-toggle]');
            const video = banner.querySelector('video');
            const playIcon = banner.querySelector('[data-play-icon]');
            const pauseIcon = banner.querySelector('[data-pause-icon]');

            if (!toggleBtn || !video || !playIcon || !pauseIcon) return;

            const updateIcons = () => {
                const isPlaying = !video.paused;
                playIcon.classList.toggle('tw-hidden', isPlaying);
                pauseIcon.classList.toggle('tw-hidden', !isPlaying);
            };

            toggleBtn.addEventListener('click', () => {
                if (video.paused) {
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
                updateIcons();
            });

            video.addEventListener('play', updateIcons);
            video.addEventListener('pause', updateIcons);
            updateIcons();
        }

        addBanners(page) {
            const pageNumber = Math.max(1, Number(page) || 1);
            const mobileOffset = this.getRowOffset(pageNumber, this.getMobileColumns());
            const desktopOffset = this.getRowOffset(pageNumber, this.getDesktopColumns());

            document.querySelectorAll(`template[id^="po-collection-banner"][data-render-page="${pageNumber}"]`).forEach(template => {
                if (template.dataset.cookie && !CookieUtils.getCookie(template.dataset.cookie)) return;
                const clone = template.content.cloneNode(true);
                const banner = clone.querySelector('.po-collection-banner')
                const style = document.createElement('style');
                const baseRowMobile = Number(banner.dataset.rowMobile);
                const baseRowDesktop = Number(banner.dataset.rowDesktop);
                const resolvedRowMobile = mobileOffset + (Number.isFinite(baseRowMobile) ? baseRowMobile : 1);
                const resolvedRowDesktop = desktopOffset + (Number.isFinite(baseRowDesktop) ? baseRowDesktop : 1);

                banner.dataset.rowMobileResolved = String(resolvedRowMobile);
                banner.dataset.rowDesktopResolved = String(resolvedRowDesktop);

                style.innerHTML = `
                    #${banner.id} {
                        grid-row: ${resolvedRowMobile};
                    }
    
                    @media (min-width: 768px) {
                        #${banner.id} {
                            grid-row: ${resolvedRowDesktop};
                        }
                    }
                `;

                banner.append(style);

                banner.querySelectorAll(".js-po-modal-btn").forEach((btn) => {
                    btn.addEventListener("click", () => {
                        this.openOverlayByClick(btn);
                    });
                });

                this.initBannerVideoController(banner);

                document.querySelector(".ais-Hits-list").append(clone);
            })

            const computedStyle = window.getComputedStyle(document.querySelector(".ais-Hits-list"));
            const rows = computedStyle.getPropertyValue("grid-template-rows").split(" ");
            //this.totalRows = rows.length;
            this.updateBannerVisibility();

        }

        resetBanners() {
            document.querySelectorAll(".po-collection-banner").forEach(banner => {
                if (banner.tagName.toLowerCase() !== "template") {
                    banner.remove();
                }
            });

            this.totalRows = 0;
        }

        isProductTileNavigationClick(event, link) {
            if (event?.defaultPrevented || !link) {
                return false;
            }

            if (
                event
                && (
                    event.button > 0
                    || event.metaKey
                    || event.ctrlKey
                    || event.shiftKey
                    || event.altKey
                )
            ) {
                return false;
            }

            const target = String(link.getAttribute("target") || "").trim().toLowerCase();
            if ((target && target !== "_self") || link.hasAttribute("download")) {
                return false;
            }

            const href = String(link.getAttribute("href") || "").trim();
            if (!href) {
                return false;
            }

            try {
                const url = new URL(href, window.location.origin);
                return url.origin === window.location.origin && /\/products\//.test(url.pathname);
            } catch (_error) {
                return href.includes("/products/");
            }
        }

        armProductTileClickIntent(link) {
            if (!link || !this.isProductTileNavigationClick(null, link)) {
                return;
            }

            window.__poAlgoliaTileClickIntentUntil = Date.now() + 2000;
            window.__poAlgoliaTileClickIntentHref = String(link.getAttribute("href") || "");
        }

        trackProductTileClickAttribution(trackSearchAttribution) {
            window.__poAlgoliaTileDirectAttributionUntil = Date.now() + 2000;
            trackSearchAttribution();
        }

        attachProductTileClickAttribution(cardElement, trackSearchAttribution) {
            if (!cardElement || typeof trackSearchAttribution !== "function") {
                return;
            }

            const getProductTilePdpLink = (event) => {
                const target = event?.target instanceof Element ? event.target : null;
                if (!target) {
                    return null;
                }

                if (
                    target.closest(
                        [
                            ".po-product-card-thumbnails-wrapper",
                            ".po-product-card-thumbnails",
                            ".mobile_button_slider",
                            ".po-product-card-controller",
                            "[data-current-variant]",
                            "button",
                            "input",
                            "select",
                            "textarea",
                        ].join(", ")
                    )
                ) {
                    return null;
                }

                const link = target.closest("a[href]");
                return this.isProductTileNavigationClick(event, link) ? link : null;
            };

            cardElement.addEventListener("pointerdown", (event) => {
                const link = getProductTilePdpLink(event);
                if (link) {
                    this.armProductTileClickIntent(link);
                }
            }, { capture: true, passive: true });

            cardElement.addEventListener("keydown", (event) => {
                const link = getProductTilePdpLink(event);
                if (link && (event.key === "Enter" || event.key === " ")) {
                    this.armProductTileClickIntent(link);
                }
            }, { capture: true });

            cardElement.addEventListener("click", (event) => {
                const link = getProductTilePdpLink(event);
                if (!link) {
                    return;
                }

                this.armProductTileClickIntent(link);
                this.trackProductTileClickAttribution(trackSearchAttribution);
            }, { capture: true });
        }

        async fetchProductCard(handle, productCardFetchUrl, productSelector, trackSearchAttribution, index, renderToken, controller, options = {}) {
            let updatedCard = null;
            let fetchRequestId = null;
            try {
                const pendingMeta = this.pendingCardSlots.get(productSelector) || {};
                const cardMeta = {
                    ...pendingMeta,
                    ...(this.getCardSlotMeta(productSelector) || {}),
                };
                const groupKey = cardMeta.groupKey || "";
                const preferredHandle = String(cardMeta.algoliaHitHandle || handle || "").trim();
                fetchRequestId = (Number(cardMeta.fetchRequestId) || 0) + 1;
                this.updateCardSlotMeta(productSelector, {
                    fetchRequestId,
                    handle: preferredHandle || handle,
                    algoliaHitHandle: preferredHandle || handle,
                    groupKey,
                    fetchUrl: productCardFetchUrl,
                    trackSearchAttribution,
                    index,
                    renderToken,
                });

                const resolvedProductCardFetchUrl = this.resolveProductCardFetchUrl(
                    productCardFetchUrl,
                    groupKey,
                    this.getLastIndexName(),
                    preferredHandle
                );
                if (resolvedProductCardFetchUrl !== productCardFetchUrl) {
                    productCardFetchUrl = resolvedProductCardFetchUrl;
                    const resolvedHandle = this.getRepresentativeGroupHandle(groupKey, this.getLastIndexName(), preferredHandle) || handle;
                    handle = resolvedHandle;
                    this.updateCardSlotMeta(productSelector, {
                        fetchUrl: productCardFetchUrl,
                        handle: resolvedHandle,
                        algoliaHitHandle: preferredHandle || resolvedHandle,
                    });
                }
                const cachedMarkup = this.getCachedCardMarkup(productCardFetchUrl);
                let cardElement = null;

                if (cachedMarkup) {
                    const template = document.createElement("template");
                    template.innerHTML = cachedMarkup.trim();
                    cardElement = template.content.firstElementChild;
                } else {
                    const response = await fetch(productCardFetchUrl, { signal: controller.signal });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const text = await response.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(text, 'text/html');
                    cardElement = doc.querySelector('#po-collection-product-card')?.firstElementChild;

                    if (cardElement) {
                        this.setCachedCardMarkup(productCardFetchUrl, cardElement.outerHTML);
                    }
                }

                const productTitle = cardElement?.querySelector('.tile-title')?.textContent.trim();

                if (!cardElement || renderToken !== this.productRenderToken) return;

                const latestMeta = this.getCardSlotMeta(productSelector) || {};
                if (latestMeta.fetchRequestId !== fetchRequestId) {
                    return;
                }

                this.attachProductTileClickAttribution(cardElement, trackSearchAttribution);

                const plpProductCard = cardElement.querySelector("po-plp-product-card");
                const algoliaProductCard = cardElement.matches?.(".po-algolia-product-card")
                    ? cardElement
                    : cardElement.querySelector(".po-algolia-product-card");
                updatedCard = algoliaProductCard || cardElement;
                if (plpProductCard && groupKey && !plpProductCard.dataset.groupKey) {
                    plpProductCard.dataset.groupKey = groupKey;
                }
                if (algoliaProductCard && groupKey && !algoliaProductCard.dataset.groupKey) {
                    algoliaProductCard.dataset.groupKey = groupKey;
                }
                if (algoliaProductCard) {
                    if (preferredHandle) {
                        algoliaProductCard.dataset.algoliaHitHandle = preferredHandle;
                    }
                    this.prioritizeProductCardVariant(
                        algoliaProductCard,
                        this.getRepresentativeGroupHandle(groupKey, this.getLastIndexName(), preferredHandle || handle)
                    );
                    this.syncRenderedProductCardVariantOrderForCard(algoliaProductCard);
                }

                poPlpSortDebug("fetchProductCard.success", {
                    handle,
                    preferredHandle,
                    productSelector,
                    groupKeyFromHit: groupKey || null,
                    groupKeyOnCard:
                        plpProductCard?.dataset?.groupKey
                        || algoliaProductCard?.dataset?.groupKey
                        || null,
                    hasPlpProductCard: Boolean(plpProductCard),
                });

                cardElement.setAttribute("data-template-name", this.template);
                const cardSlot = document.querySelector(`#${productSelector}`);

                if (cardSlot) {
                    cardSlot.innerHTML = "";
                    cardSlot.append(cardElement);
                    cardSlot.dataset.cardFetchState = "loaded";
                    cardSlot.dataset.cardVirtualized = "false";

                    cardSlot.style.contentVisibility = "auto";
                    cardSlot.style.containIntrinsicSize = "0 400px";

                    this.captureCardSlotState(cardSlot);
                    this.observeVirtualCardSlot(cardSlot);
                    this.syncRenderedPriceForItem(cardSlot.closest("li"), this.getLastIndexName());
                    this.dedupeRenderedSearchHits();
                    this.dedupeRenderedUngroupedCollectionItems();

                    requestAnimationFrame(() => {
                        this.syncPlpThumbnailStatusForCard(algoliaProductCard);
                    });

                    const listItem = cardSlot.closest(".ais-Hits-item");
                    const visibleItems = window.getVisiblePlpHitItems?.() || [];
                    const visibleIndex = listItem ? visibleItems.indexOf(listItem) : -1;
                    const productsPerPage = Math.max(
                        1,
                        Number(this.productsPerPage) || window.getPlpProductsPerPage?.() || 8
                    );
                    const isLoadMoreBatch =
                        listItem?.dataset?.poPlpLoadMoreBatch != null
                        && listItem.dataset.poPlpLoadMoreBatch !== "";
                    const isFirstPageBatch = visibleIndex > -1 && visibleIndex < productsPerPage;

                    const priceRoot = plpProductCard || algoliaProductCard;
                    if (priceRoot && (isLoadMoreBatch || isFirstPageBatch)) {
                        window.refreshPlpPricesForRoots?.([priceRoot]);
                    } else if (listItem && (isLoadMoreBatch || isFirstPageBatch)) {
                        window.refreshPlpPricesForListItems?.([listItem]);
                    }

                    if (listItem) {
                        window.PoDkStorefrontPrices?.refreshWithin?.(
                            algoliaProductCard || cardElement
                        );
                    }

                    document.dispatchEvent(new CustomEvent("plp:card-rendered", {
                        detail: { card: algoliaProductCard || cardElement }
                    }));
                }

                if (this.template !== "search") return;

                const setupImageAnalytics = () => {
                    const imgWrappers = cardElement.querySelectorAll(
                        "[data-pd-image-wrapper], [data-pd-image-hover-wrapper]"
                    );
                    
                    if (!imgWrappers.length) return false;
                    
                    const productSku = cardElement
                        .querySelector(".po-algolia-product-card")
                        ?.dataset.firstVariantSku;
                    const description = `${AlgoliaPoCustomSearch.validateDateAnalytics(productTitle)}_${productSku}`;
                    const elementId = `Tiles_Tile${index}`;
                    
                    for (const wrapper of imgWrappers) {
                        wrapper.setAttribute("data-element-id", elementId);
                        wrapper.setAttribute("data-description", description);
                    }

                    return true;
                };

                setupImageAnalytics();
            } catch (error) {
                if (this.isAbortError(error)) {
                    return;
                }

                console.error('Error fetching product card:', error);

                // Remove product card if fetch fails
                document.querySelector(`#${productSelector}`)?.closest("li.ais-Hits-item")?.remove();
            } finally {
                this.pendingCardControllers.delete(controller);

                if (renderToken !== this.productRenderToken) {
                    return;
                }

                const latestMeta = this.getCardSlotMeta(productSelector) || {};
                if (fetchRequestId !== null && latestMeta.fetchRequestId !== fetchRequestId) {
                    return;
                }

                if (options.countProduct !== false) {
                    this.productsAdded++;
                    if (!this.firstPageRendered) {
                        this.firstPageRendered = true;

                        if (this.getDisplayProductHits() >= this.minResultsForBanners) {
                            this.addBanners(this.getCurrentPage() || 1);
                        }
                    }
                }
            }
        }

        openOverlayByClick(btn) {
            const modalId = btn.getAttribute("data-modal-id");
            const target = modalId ? document.getElementById(modalId) : null;
            if (target && typeof target.showModal === "function") {
                if (target.dataset.position === "right") target.classList.add('tw-animate-slide-left');
                target.showModal();
            }
        }

        onCardsLoaded (renderToken = this.productRenderToken) {
            if (renderToken !== this.productRenderToken) {
                return;
            }

            // Keep the selected index's order when prices change or another page is appended.
            this.syncRenderedGroupPrices(this.getLastIndexName());
            this.dedupeRenderedSearchHits();
            this.dedupeRenderedUngroupedCollectionItems();
            if (this.template === "search") {
                this.syncSearchProductsForTracking();
                this.syncSearchResultCounts(this.getCurrentSearchQuery());
                this.updateTabsAndOptionsVisibility();
            }
            this.showAisPage();
            this.cardPromises.length = 0;
            const hits = this.firstPageHits;
            this.firstPageHits = [];

            document.dispatchEvent(new CustomEvent('plp:cards-loaded', { detail: {
                hits,
                maxHits: this.getDisplayProductHits()
            } }));

            const productsPerPage = Math.max(1, Number(this.productsPerPage) || 0);
            const renderedCount = this.getRenderedProductCount();
            document.dispatchEvent(
                new CustomEvent("plp:page-batch-appended", {
                    detail: {
                        fromIndex: 0,
                        batchSize: Math.min(renderedCount, productsPerPage),
                    },
                })
            );
        }

        normalizeFacetPayload(rawFacets = null) {
            if (Array.isArray(rawFacets)) {
                return rawFacets;
            }

            if (rawFacets && typeof rawFacets === "object") {
                return Object.entries(rawFacets).map(([name, data]) => ({ name, data }));
            }

            return [];
        }

        dispatchCurrentFacetsLoaded(result = null) {
            const resultFacets = this.normalizeFacetPayload(result?.facets);
            const helperFacets = this.normalizeFacetPayload(
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.facets
            );
            const facets = resultFacets.length
                ? resultFacets
                : helperFacets.length
                    ? helperFacets
                    : this.facets;
            this.facets = facets;

            document.dispatchEvent(new CustomEvent("plp:facets-loaded", {
                detail: {
                    facets,
                    nbHits: this.lastNbHits,
                    maxHits: this.getDisplayProductHits(),
                    showResults: true,
                    facetOrdering: result?.renderingContent?.facetOrdering?.facets?.order
                }
            }));
        }

        shouldUseCompactGroupHits(indexName = this.getLastIndexName()) {
            return this.template === "collection"
                && !this.useDynamicPlpGroupCards
                && !document.querySelector("po-plp-product-card")
                && !this.isPriceSortIndex(indexName);
        }

        getLoadingSkeletonCount() {
            if (this.template !== "collection") {
                return 0;
            }

            const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
            const columns = isDesktop ? this.getDesktopColumns() : this.getMobileColumns();
            const visibleRows = isDesktop ? 3 : 4;
            const productsPerPage = this.getProductsPerPage();

            return Math.max(1, Math.min(productsPerPage, columns * visibleRows));
        }

        getProductCardSkeletonMarkup({ includeSlotWrapper = false, slotId = "", cardFetchState = "idle" } = {}) {
            const innerMarkup = `
                <div class="po-card-fetch-skeleton" aria-hidden="true">
                    <div class="tw-po-tile tw-border-none tw-gap-0 [&_.po-content-wrapper]:tw-p-md [&_.po-content-wrapper]:tw-pb-0 [&_.po-content-wrapper]:tw-gap-[2px]">
                        <div class="po-img-wrapper tw-min-w-full tw-po-cis-auto">
                            <div class="po-card-fetch-skeleton-image-shell tw-bg-po-secondary tw-relative tw-flex tw-flex-col tw-items-stretch sd:tw-justify-start tw-aspect-square">
                                <div class="po-card-fetch-skeleton-header tw-relative tw-w-full tw-min-h-32 tb:tw-min-h-52">
                                    <span class="po-card-fetch-skeleton-chip po-card-fetch-skeleton-chip--left"></span>
                                    <span class="po-card-fetch-skeleton-chip po-card-fetch-skeleton-chip--right"></span>
                                </div>
                                <div class="po-card-fetch-skeleton-slider">
                                    <span class="po-card-fetch-skeleton-slide"></span>
                                </div>
                                <div class="po-card-fetch-skeleton-controller po-card-fetch-skeleton-controller--desktop">
                                    <span class="po-card-fetch-skeleton-counter"></span>
                                </div>
                                <div class="po-card-fetch-skeleton-controller po-card-fetch-skeleton-controller--mobile">
                                    <span class="po-card-fetch-skeleton-nav"></span>
                                    <span class="po-card-fetch-skeleton-counter po-card-fetch-skeleton-counter--mobile"></span>
                                    <span class="po-card-fetch-skeleton-nav"></span>
                                </div>
                                <div class="po-card-fetch-skeleton-thumbnails-wrapper">
                                    <div class="po-card-fetch-skeleton-thumbnails">
                                        <span class="po-card-fetch-skeleton-thumb">
                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                        </span>
                                        <span class="po-card-fetch-skeleton-thumb">
                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                        </span>
                                        <span class="po-card-fetch-skeleton-thumb">
                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="po-content-wrapper po-card-fetch-skeleton-content">
                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--title"></span>
                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--price"></span>
                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--caption"></span>
                        </div>
                    </div>
                </div>
            `;

            if (!includeSlotWrapper) {
                return innerMarkup;
            }

            return `
                <div id="${slotId}" class="po-card-fetch-slot" data-card-fetch-state="${cardFetchState}" data-card-virtualized="false">
                    ${innerMarkup}
                </div>
            `;
        }

        getInitialGridSkeletonMarkup(html) {
            const skeletonCount = this.getLoadingSkeletonCount();

            if (skeletonCount < 1) {
                return "";
            }

            return html`
                <div class="po-plp-grid-skeleton" data-plp-grid-skeleton>
                    <ol class="po-plp-grid-skeleton-list" aria-hidden="true">
                        ${Array.from({ length: skeletonCount }, () => html`
                            <li class="ais-Hits-item">
                                <div class="po-card-fetch-skeleton" aria-hidden="true">
                                    <div class="tw-po-tile tw-border-none tw-gap-0 [&_.po-content-wrapper]:tw-p-md [&_.po-content-wrapper]:tw-pb-0 [&_.po-content-wrapper]:tw-gap-[2px]">
                                        <div class="po-img-wrapper tw-min-w-full tw-po-cis-auto">
                                            <div class="po-card-fetch-skeleton-image-shell tw-bg-po-secondary tw-relative tw-flex tw-flex-col tw-items-stretch sd:tw-justify-start tw-aspect-square">
                                                <div class="po-card-fetch-skeleton-header tw-relative tw-w-full tw-min-h-32 tb:tw-min-h-52">
                                                    <span class="po-card-fetch-skeleton-chip po-card-fetch-skeleton-chip--left"></span>
                                                    <span class="po-card-fetch-skeleton-chip po-card-fetch-skeleton-chip--right"></span>
                                                </div>
                                                <div class="po-card-fetch-skeleton-slider">
                                                    <span class="po-card-fetch-skeleton-slide"></span>
                                                </div>
                                                <div class="po-card-fetch-skeleton-controller po-card-fetch-skeleton-controller--desktop">
                                                    <span class="po-card-fetch-skeleton-counter"></span>
                                                </div>
                                                <div class="po-card-fetch-skeleton-controller po-card-fetch-skeleton-controller--mobile">
                                                    <span class="po-card-fetch-skeleton-nav"></span>
                                                    <span class="po-card-fetch-skeleton-counter po-card-fetch-skeleton-counter--mobile"></span>
                                                    <span class="po-card-fetch-skeleton-nav"></span>
                                                </div>
                                                <div class="po-card-fetch-skeleton-thumbnails-wrapper">
                                                    <div class="po-card-fetch-skeleton-thumbnails">
                                                        <span class="po-card-fetch-skeleton-thumb">
                                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                                        </span>
                                                        <span class="po-card-fetch-skeleton-thumb">
                                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                                        </span>
                                                        <span class="po-card-fetch-skeleton-thumb">
                                                            <span class="po-card-fetch-skeleton-thumb-label"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-badge"></span>
                                                            <span class="po-card-fetch-skeleton-thumb-image"></span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="po-content-wrapper po-card-fetch-skeleton-content">
                                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--title"></span>
                                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--price"></span>
                                            <span class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--caption"></span>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        `)}
                    </ol>
                </div>
            `;
        }

        setGridSkeletonVisibility(isVisible) {
            if (this.template !== "collection") {
                return;
            }

            const gridSkeleton = document.querySelector("[data-plp-grid-skeleton]");
            const hitsContainer = document.querySelector(".ais-hits-container");

            if (gridSkeleton) {
                gridSkeleton.style.display = isVisible ? "block" : "none";
            }

            if (hitsContainer) {
                hitsContainer.style.display = isVisible ? "none" : "";
            }
        }

        addProductCard(hit, trackSearchAttribution, options = {}) {
            const { queueObservation = true } = options;
            const productHandle = hit.handle;
            const groupKey = hit?.meta?.custom?.x_groupkey || "";
            const isUngroupedCollection = this.isCollectionUngrouped();
            const currentCollectionHandle = this.getCurrentCollectionHandle();
            const activeFacetFilters = this.normalizeFacetFilters(this.activeFacetFilters);
            const helperFacetFilters = this.normalizeFacetFilters(
                window.algoliaShopify?.instantsearch?.search?.helper?.state?.facetFilters
            );
            const effectiveFacetFilters = activeFacetFilters.length > 0
                ? activeFacetFilters
                : helperFacetFilters;
            const hasScopedCollectionFilters = this.hasScopedCollectionQueryFilters();
            const shouldLimitVariantsForFilteredCollection = this.template === "collection"
                && !isUngroupedCollection
                && (
                    this.hasActiveFacetFilters(effectiveFacetFilters)
                    || hasScopedCollectionFilters
                );
            const productCardUrl = new URL(
                `${Shopify.routes.root}products/${encodeURIComponent(hit.handle)}`,
                window.location.origin
            );
            productCardUrl.searchParams.set("view", "po-plp-card");
            if (this.isPriceSortIndex() && /^[1-9]\d*$/.test(String(hit.objectID || ""))) {
                productCardUrl.searchParams.set("variant", String(hit.objectID));
            }
            if (this.template === "collection" && !isUngroupedCollection && currentCollectionHandle) {
                productCardUrl.searchParams.set("po_plp_collection", currentCollectionHandle);
            }
            if (isUngroupedCollection) {
                productCardUrl.searchParams.set("ungrouped", "true");
            }
            if (this.template === "search") {
                productCardUrl.searchParams.set("show_variant", "false");
                productCardUrl.searchParams.set("search_mode", "true");
            }
            if (shouldLimitVariantsForFilteredCollection) {
                productCardUrl.searchParams.set("po_plp_filtered", "1");
            }
            this.applyGroupSelectionToProductCardUrl(productCardUrl, groupKey, this.getLastIndexName(), productHandle);
            this.appendPoDkCodesToUrl(productCardUrl);
            const productCardFetchUrl = productCardUrl.toString();
            const timestamp = Date.now();
            const productSelector = `po-product-card-${hit.handle}-${hit.objectID}-${timestamp}`;
            const renderToken = this.productRenderToken;

            this.pendingCardSlots.set(productSelector, {
                handle: productHandle,
                algoliaHitHandle: productHandle,
                groupKey,
                fetchUrl: productCardFetchUrl,
                trackSearchAttribution,
                index: hit.productPosition,
                renderToken,
            });
            this.updateCardSlotMeta(productSelector, {
                handle: productHandle,
                algoliaHitHandle: productHandle,
                groupKey,
                fetchUrl: productCardFetchUrl,
                trackSearchAttribution,
                renderToken,
                index: hit.productPosition,
                currentMarkup: "",
                measuredHeight: 0,
                currentVariantHandle: null,
                sliderScrollLeft: 0,
                thumbnailScrollLeft: 0,
                isVirtualized: false,
            });

            poPlpSortDebug("addProductCard", {
                handle: productHandle,
                groupKey,
                objectID: hit?.objectID || null,
                productPosition: hit?.productPosition || null,
                queueObservation,
                hit: getSortDebugHitSummary(hit),
            });

            if (queueObservation) {
                this.queueObserveCardSlot(productSelector);
            }

            return this.getProductCardSkeletonMarkup({
                includeSlotWrapper: true,
                slotId: productSelector,
                cardFetchState: "idle",
            });
        }

        setSearchLocalStorage() {
            const searchInput = this.getCurrentSearchQuery();
            if (!searchInput) return;
            const savedTerms = JSON.parse(localStorage.getItem("po-search-history") || "[]");
            const filteredTerms = [...new Set(savedTerms)].slice(0, 5);

            // Add the new search input to the beginning of the array
            filteredTerms.unshift(searchInput);
            const uniqueTerms = [...new Set(filteredTerms)];

            localStorage.setItem("po-search-history", JSON.stringify(uniqueTerms));
        }

        formatFacetValueForTealium(filter) {
            const rawFilter = (filter || "").toString().trim();
            if (!rawFilter) return "";

            const separatorIndex = rawFilter.indexOf(":");
            if (separatorIndex === -1) return rawFilter.replace(/\s+/g, "");

            const rawFacetName = rawFilter.slice(0, separatorIndex).trim();
            const facetValue = rawFilter.slice(separatorIndex + 1);
            const facetName = rawFacetName.replace(/^meta\.custom\./, "");

            return `${facetName}=${facetValue}`.replace(/\s+/g, "");
        }

        formatFacetFiltersToString(facetFilters = []) {
            const flattenedFilters = [];

            (facetFilters || []).forEach((filter) => {
                if (Array.isArray(filter)) {
                    filter.forEach((entry) => flattenedFilters.push(entry));
                    return;
                }

                flattenedFilters.push(filter);
            });

            return flattenedFilters
                .map((filter) => this.formatFacetValueForTealium(filter))
                .filter(Boolean)
                .join("|");
        }

        normalizeFacetFilters(facetFilters) {
            if (!facetFilters) return [];
            if (Array.isArray(facetFilters)) return facetFilters;
            if (typeof facetFilters === "string") {
                try {
                    const parsed = JSON.parse(facetFilters);
                    return Array.isArray(parsed) ? parsed : [];
                } catch (_e) {
                    return [];
                }
            }
            return [];
        }

        extractRuleContextsFromParams(urlSearchParams) {
            if (!(urlSearchParams instanceof URLSearchParams)) {
                return [];
            }

            const repeatedContexts = urlSearchParams.getAll("ruleContexts");
            if (repeatedContexts.length > 1) {
                return repeatedContexts
                    .map((value) => String(value || "").trim())
                    .filter(Boolean);
            }

            const single = repeatedContexts[0];
            if (single) {
                try {
                    const parsed = JSON.parse(single);
                    if (Array.isArray(parsed)) {
                        return parsed.map((value) => String(value || "").trim()).filter(Boolean);
                    }
                } catch (_error) {
                    // not JSON-stringified, treat as single context value
                }
                return [String(single).trim()].filter(Boolean);
            }

            const bracketContexts = [];
            urlSearchParams.forEach((value, key) => {
                if (/^ruleContexts\[\d+\]$/.test(key)) {
                    bracketContexts.push(String(value || "").trim());
                }
            });
            return bracketContexts.filter(Boolean);
        }

        hasActiveFacetFilters(facetFilters = this.activeFacetFilters) {
            const filters = this.normalizeFacetFilters(facetFilters);
            if (!filters.length) return false;

            return filters.some((entry) =>
                Array.isArray(entry) ? entry.some(Boolean) : Boolean(entry)
            );
        }

        isPriceSortIndex(indexName = this.getLastIndexName()) {
            const normalized = String(indexName || "").trim().toLowerCase();
            if (!normalized) return false;

            return (
                normalized.includes("price_asc") ||
                normalized.includes("price_desc") ||
                normalized.includes(".price_asc") ||
                normalized.includes(".price_desc")
            );
        }

        getPriceSortDirection(indexName = this.getLastIndexName()) {
            const normalized = String(indexName || "").trim().toLowerCase();
            if (normalized.includes("price_desc") || normalized.includes(".price_desc")) {
                return "desc";
            }

            if (normalized.includes("price_asc") || normalized.includes(".price_asc")) {
                return "asc";
            }

            return null;
        }

        getComparableHitPrice(hit = {}, direction = this.getPriceSortDirection()) {
            return this.getDirectHitPrice(hit, direction);
        }

        getDirectHitPrice(hit = {}, direction = this.getPriceSortDirection()) {
            const currency = this.getActiveCurrency()?.toLowerCase?.() || "";
            const marketPricing = currency ? hit?.[`market_pricing_${currency}`] : null;
            for (const value of [marketPricing?.price, hit?.price]) {
                if (value == null || String(value).trim() === "") continue;
                const price = Number(value);
                if (Number.isFinite(price) && price >= 0) return price;
            }
            return direction === "desc" ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
        }

        sortHitsForPriceIndex(hits = [], indexName = this.getLastIndexName()) {
            const direction = this.getPriceSortDirection(indexName);
            const rxFilteredHits = window.poPlpRxUtils?.filterNonRxHits(hits) ?? hits;

            if (!direction || !Array.isArray(rxFilteredHits) || rxFilteredHits.length < 2) {
                return rxFilteredHits;
            }

            return rxFilteredHits
                .map((hit, originalIndex) => ({
                    hit,
                    originalIndex,
                    comparablePrice: this.getComparableHitPrice(hit, direction),
                    directPrice: this.getDirectHitPrice(hit, direction),
                }))
                .sort((left, right) => {
                    const diff = compareNumericSortValues(
                        left.comparablePrice,
                        right.comparablePrice,
                        direction
                    );

                    if (diff !== 0) {
                        return diff;
                    }

                    const directDiff = compareNumericSortValues(
                        left.directPrice,
                        right.directPrice,
                        direction
                    );

                    if (directDiff !== 0) {
                        return directDiff;
                    }

                    return left.originalIndex - right.originalIndex;
                })
                .map(({ hit }) => hit);
        }

        sortGroupsForPriceIndex(groups = [], indexName = this.getLastIndexName()) {
            const direction = this.getPriceSortDirection(indexName);

            if (!direction || !Array.isArray(groups) || groups.length < 2) {
                return groups;
            }

            return groups
                .map((group, originalIndex) => {
                    const representativeHit = Array.isArray(group?.hits) && group.hits.length > 0
                        ? group.hits[0]
                        : null;

                    return {
                        group,
                        originalIndex,
                        comparablePrice: this.getComparableHitPrice(representativeHit, direction),
                        directPrice: this.getDirectHitPrice(representativeHit, direction),
                    };
                })
                .sort((left, right) => {
                    const diff = compareNumericSortValues(
                        left.comparablePrice,
                        right.comparablePrice,
                        direction
                    );

                    if (diff !== 0) {
                        return diff;
                    }

                    const directDiff = compareNumericSortValues(
                        left.directPrice,
                        right.directPrice,
                        direction
                    );

                    if (directDiff !== 0) {
                        return directDiff;
                    }

                    return left.originalIndex - right.originalIndex;
                })
                .map(({ group }) => group);
        }

        getRenderedGroupKeyForItem(item) {
            if (!item) {
                return null;
            }

            if (this.isCollectionUngrouped()) {
                return null;
            }

            const inlineGroupKey =
                item.dataset?.groupKey ||
                item.querySelector("po-plp-product-card")?.dataset?.groupKey ||
                item.querySelector(".po-algolia-product-card")?.dataset?.groupKey;

            if (inlineGroupKey) {
                return inlineGroupKey;
            }

            const slotId = item.querySelector(".po-card-fetch-slot")?.id;
            if (!slotId) {
                return null;
            }

            return (
                this.getCardSlotMeta(slotId)?.groupKey ||
                this.pendingCardSlots.get(slotId)?.groupKey ||
                null
            );
        }

        getRenderedSearchHandleForItem(item) {
            if (!item) {
                return "";
            }

            const algoliaCard = item.matches?.(".po-algolia-product-card")
                ? item
                : item.querySelector(".po-algolia-product-card");
            const algoliaHandle = algoliaCard?.dataset?.laDnProductHandle;

            if (algoliaHandle) {
                return algoliaHandle;
            }

            const plpCard = item.querySelector("po-plp-product-card");
            const plpHandle = plpCard?.dataset?.pdHandle;

            if (plpHandle) {
                return plpHandle;
            }

            const slotId = item.querySelector(".po-card-fetch-slot")?.id;
            if (!slotId) {
                return "";
            }

            return (
                this.getCardSlotMeta(slotId)?.handle ||
                this.pendingCardSlots.get(slotId)?.handle ||
                ""
            );
        }

        dedupeRenderedUngroupedCollectionItems() {
            if (!this.isCollectionUngrouped()) {
                return;
            }

            const hitsListEl = document.querySelector(".ais-Hits-list");
            if (!hitsListEl) {
                return;
            }

            const fullHitsByHandle = this.getCachedPlpFullHitsByHandle();
            const seenColorKeys = new Set();

            hitsListEl.querySelectorAll(":scope > .ais-Hits-item").forEach((item) => {
                const handle = this.getRenderedSearchHandleForItem(item)?.trim()?.toLowerCase();
                const fullHit = handle ? fullHitsByHandle.get(handle) : null;
                const colorKey = this.getHitColorKey(fullHit || { handle });

                if (!colorKey) {
                    return;
                }

                if (seenColorKeys.has(colorKey)) {
                    item.dataset.ungroupedDuplicate = "true";
                    this.syncRenderedSearchItemVisibility(item);
                    return;
                }

                seenColorKeys.add(colorKey);
                delete item.dataset.ungroupedDuplicate;
                this.syncRenderedSearchItemVisibility(item);
            });
        }

        dedupeRenderedSearchHits() {
            if (this.template !== "search") {
                return;
            }

            const hitsListEl = document.querySelector(".ais-Hits-list");
            if (!hitsListEl) {
                return;
            }

            const seenHandles = new Set();

            hitsListEl.querySelectorAll(":scope > .ais-Hits-item").forEach((item) => {
                const handle = this.getRenderedSearchHandleForItem(item)?.trim()?.toLowerCase();

                if (!handle) {
                    return;
                }

                if (seenHandles.has(handle)) {
                    item.dataset.searchDuplicate = "true";
                    this.syncRenderedSearchItemVisibility(item);
                    return;
                }

                seenHandles.add(handle);
                delete item.dataset.searchDuplicate;
                this.syncRenderedSearchItemVisibility(item);
            });

        }

        syncRenderedSearchItemVisibility(item) {
            if (!item) {
                return;
            }

            const isDuplicate = item.dataset.searchDuplicate === "true";
            const isUngroupedDuplicate = item.dataset.ungroupedDuplicate === "true";
            delete item.dataset.searchWindowHidden;
            const shouldHide = isDuplicate || isUngroupedDuplicate;

            item.hidden = shouldHide;

            if (shouldHide) {
                item.setAttribute("aria-hidden", "true");
                item.style.display = "none";
                return;
            }

            item.removeAttribute("aria-hidden");
            item.style.removeProperty("display");
        }

        getResolvedGroupHits(groupKey) {
            if (!groupKey) {
                return [];
            }

            const groupHits = this.getCachedPlpGroupsByKey().get(groupKey) || [];
            const fullHitsByHandle = this.getCachedPlpFullHitsByHandle();

            return groupHits
                .map((hit) => {
                    const handle = hit?.handle;
                    return (handle && fullHitsByHandle.get(handle)) || hit || null;
                })
                .filter(Boolean);
        }

        getRepresentativeGroupHit(groupKey, indexName = this.getLastIndexName()) {
            const resolvedHits = this.getResolvedGroupHits(groupKey);
            const sortedHits = this.sortHitsForPriceIndex(resolvedHits, indexName);
            return sortedHits[0] || null;
        }

        getRenderedPriceTargets(card, tile) {
            return [
                ...new Set([
                    ...(tile ? Array.from(tile.querySelectorAll("[data-po-price]")) : []),
                    ...(card ? Array.from(card.querySelectorAll("[data-po-price]")) : []),
                ]),
            ].filter((element) => !element.querySelector("[data-po-price]"));
        }

        syncRenderedPriceForItem(_item, _indexName = this.getLastIndexName()) {
            // Liquid owns the displayed variant price and discounts; sorting never overwrites it.
        }

        syncRenderedGroupPrices(indexName = this.getLastIndexName()) {
            if (!this.isPriceSortIndex(indexName)) {
                return;
            }

            document.querySelectorAll(".ais-Hits-list > .ais-Hits-item").forEach((item) => {
                this.syncRenderedPriceForItem(item, indexName);
            });
        }

        getCurrentCollectionHandle() {
            return String(window.collection?.handle || "").trim().toLowerCase();
        }

        getPlpCollectionHandle() {
            return String(window.collection?.plp_handle || "").trim().toLowerCase();
        }

        getCurrentCollectionId() {
            return String(
                window.algoliaShopify?.shopify?.collection_id ||
                window.algoliaShopify?.current_collection_id ||
                window.collection?.id ||
                ""
            ).trim();
        }

        escapeAlgoliaFilterValue(value) {
            return String(value || "")
                .replace(/\\/g, "\\\\")
                .replace(/"/g, "\\\"");
        }

        loadNonRxVariantFilter() {
            if (!this.nonRxVariantFilterPromise) {
                // Exclude RX before distinct chooses a cheaper frame-only variant for the card.
                const params = new URLSearchParams({
                    facets: JSON.stringify(["options.frame_size"]),
                    hitsPerPage: "0", distinct: "0", maxValuesPerFacet: "1000", enableRules: "false",
                });
                this.nonRxVariantFilterPromise = fetch(this.getQueryUrl(), {
                    method: "POST",
                    body: JSON.stringify({ requests: [{ indexName: this.getDefaultProductsIndex(), params: params.toString() }] }),
                }).then((response) => response.json()).then((data) => {
                    const sizes = data.results?.[0]?.facets?.["options.frame_size"];
                    if (!sizes) throw new Error("Missing frame size facets");
                    this.nonRxVariantFilter = Object.keys(sizes)
                        .filter((size) => size.toUpperCase().includes("RX"))
                        .map((size) => `NOT options.frame_size:"${this.escapeAlgoliaFilterValue(size)}"`)
                        .join(" AND ");
                }).catch((error) => {
                    this.nonRxVariantFilterPromise = null;
                    console.warn("Could not load PLP frame size filters:", error);
                });
            }
            return this.nonRxVariantFilterPromise;
        }

        getCurrentCollectionFilter(collectionHandle = this.getPlpCollectionHandle() || this.getCurrentCollectionHandle()) {
            const normalizedHandle = String(collectionHandle || "").trim().toLowerCase();
            if (normalizedHandle) {
                return [`collections:${normalizedHandle}`, this.nonRxVariantFilter].filter(Boolean).join(" AND ");
            }

            const collectionId = this.getCurrentCollectionId();
            const collectionIdIndexing = window.algoliaShopify?.config?.collection_id_indexing;
            const usesCollectionIdIndexing = Boolean(
                collectionId &&
                collectionIdIndexing &&
                String(collectionIdIndexing).toLowerCase() !== "false"
            );

            if (usesCollectionIdIndexing) {
                return [`collection_ids:"${this.escapeAlgoliaFilterValue(collectionId)}"`, this.nonRxVariantFilter].filter(Boolean).join(" AND ");
            }

            return "";
        }

        getCurrentQueryFilters() {
            const filtersFromFetchParams = this.fetchParams
                ? new URLSearchParams(this.fetchParams).get("filters")
                : "";
            const filtersFromHelperState = window.algoliaShopify?.instantsearch?.search?.helper?.state?.filters;
            const filtersFromLastResults = new URLSearchParams(
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.params || ""
            ).get("filters");

            return String(
                filtersFromFetchParams
                || filtersFromHelperState
                || filtersFromLastResults
                || ""
            ).trim();
        }

        getCollectionFilterHandles(filtersValue = this.getCurrentQueryFilters()) {
            const normalizedFilters = String(filtersValue || "").trim();
            if (!normalizedFilters) {
                return [];
            }

            const handles = [];
            const matcher = /collections:([^\s)]+)/gi;
            let match = matcher.exec(normalizedFilters);

            while (match) {
                const normalizedHandle = String(match[1] || "").trim().toLowerCase();
                if (normalizedHandle && !handles.includes(normalizedHandle)) {
                    handles.push(normalizedHandle);
                }
                match = matcher.exec(normalizedFilters);
            }

            return handles;
        }

        hasScopedCollectionQueryFilters(filtersValue = this.getCurrentQueryFilters()) {
            if (this.template !== "collection" || this.isCollectionUngrouped()) {
                return false;
            }

            const plpCollectionHandle = this.getPlpCollectionHandle();
            const collectionFilterHandles = this.getCollectionFilterHandles(filtersValue);

            if (collectionFilterHandles.length === 0) {
                return false;
            }

            if (!plpCollectionHandle) {
                return collectionFilterHandles.length > 0;
            }

            return collectionFilterHandles.some((handle) => handle !== plpCollectionHandle);
        }

        shouldScopeGroupHitsToCurrentCollection() {
            if (this.template !== "collection" || this.isCollectionUngrouped()) {
                return false;
            }

            const currentCollectionHandle = this.getCurrentCollectionHandle();
            const plpCollectionHandle = this.getPlpCollectionHandle();
            const collectionFilter = this.getCurrentCollectionFilter(plpCollectionHandle || currentCollectionHandle);

            // Scope group hits whenever we are on a grouped collection page with
            // a known collection filter, so that only variants belonging to the
            // current collection are fetched and shown in the variant carousel.
            return Boolean(collectionFilter);
        }

        getScopedGroupFilters(baseFilters = "") {
            const normalizedBaseFilters = String(baseFilters || "").trim();

            if (!this.shouldScopeGroupHitsToCurrentCollection()) {
                return normalizedBaseFilters;
            }

            const collectionHandle = this.getPlpCollectionHandle() || this.getCurrentCollectionHandle();
            const currentCollectionClause = this.getCurrentCollectionFilter(collectionHandle);
            if (!currentCollectionClause) {
                return normalizedBaseFilters;
            }

            if (!normalizedBaseFilters) {
                return currentCollectionClause;
            }

            if (normalizedBaseFilters.toLowerCase().includes(currentCollectionClause.toLowerCase())) {
                return normalizedBaseFilters;
            }

            return `(${normalizedBaseFilters}) AND (${currentCollectionClause})`;
        }

        shouldLoadProductGroupsForIndex(indexName = this.getLastIndexName(), facetFilters = this.activeFacetFilters) {
            if (this.template === "search" || this.isCollectionUngrouped()) {
                return false;
            }

            if (this.template === "collection") {
                return true;
            }

            return (
                this.hasActiveFacetFilters(facetFilters)
                || this.isPriceSortIndex(indexName)
                || this.hasScopedCollectionQueryFilters()
                || this.shouldScopeGroupHitsToCurrentCollection()
            );
        }

        normalizeCurrency(value) {
            if (typeof value !== "string") return null;
            const trimmed = value.trim();
            return trimmed ? trimmed.toUpperCase() : null;
        }

        normalizeCountryCode(value) {
            if (typeof value !== "string") return null;
            const trimmed = value.trim();
            return /^[A-Z]{2}$/i.test(trimmed) ? trimmed.toUpperCase() : null;
        }

        getActiveCountry() {
            const htmlLangCountry = document?.documentElement?.lang?.split?.("-")?.[1];
            const localeCountry = window.SHOP_LOCALES?.current?.iso_code?.split?.("-")?.[1];
            const candidates = [
                window.Shopify?.country,
                document?.documentElement?.dataset?.countryCode,
                htmlLangCountry,
                localeCountry,
            ];

            for (const candidate of candidates) {
                const normalized = this.normalizeCountryCode(candidate);
                if (normalized) return normalized;
            }

            return null;
        }

        getActiveCurrency() {
            const candidates = [
                window.Shopify?.currency?.active,
                window.Shopify?.currency?.currency_code,
                window.Shopify?.currency?.iso_code,
                window.ShopifyAnalytics?.meta?.currency,
                window.Shopify?.analytics?.meta?.currency,
                window.algoliaShopify?.shopify?.currency,
                window.algoliaShopify?.shopify?.market?.currency,
                window.algoliaShopify?.shopify?.market?.currency_code,
                window.algoliaShopify?.shopify?.market?.currencyCode,
            ];

            for (const candidate of candidates) {
                const normalized = this.normalizeCurrency(candidate);
                if (normalized) return normalized;
            }

            const fallbackCountry = this.getActiveCountry();
            return COUNTRY_TO_CURRENCY_FALLBACK[fallbackCountry] || (fallbackCountry ? "EUR" : null);
        }

        getCurrencyReplicaIndex(currency, preferredDirection = null) {
            const normalized = this.normalizeCurrency(currency);
            if (!normalized) return null;

            const lower = normalized.toLowerCase();
            const normalizedDirection = ["asc", "desc"].includes(preferredDirection)
                ? preferredDirection
                : null;
            const options = filterSortOrdersByAllowlist(
                this.sortOrders || window.algoliaShopify?.sortOrders || []
            );
            const currentMarket = window.poAlgoliaUtils?.getCurrentMarket?.();
            const expectedReplicaIndex = normalizedDirection
                ? window.poAlgoliaUtils?.buildPricingReplicaIndex?.({
                    currency: lower,
                    direction: normalizedDirection,
                    indexPrefix: window.algoliaShopify?.config?.index_prefix,
                    marketSuffix: currentMarket?.suffix,
                })
                : null;
            const matches = [];

            options.forEach((option) => {
                const rawValue = typeof option === "string" ? option : option?.value;
                const value = window.poAlgoliaUtils?.normalizeIndexName?.(rawValue) || rawValue;
                if (typeof value !== "string") return;
                if (
                    value.includes(`market_pricing_${lower}.price_`) ||
                    value.includes(`market_pricing_${lower}_price_`)
                ) {
                    matches.push(value);
                }
            });

            if (!matches.length) {
                return expectedReplicaIndex || null;
            }

            if (expectedReplicaIndex) {
                const exactMatch = matches.find(
                    (value) => String(value).trim().toLowerCase() === expectedReplicaIndex.toLowerCase()
                );

                if (exactMatch) {
                    return exactMatch;
                }
            }

            if (normalizedDirection) {
                const preferredMatch = matches.find((value) => value.endsWith(`_${normalizedDirection}`));
                if (preferredMatch) {
                    return preferredMatch;
                }
            }

            return (
                expectedReplicaIndex ||
                matches.find((value) => value.endsWith("_desc")) ||
                matches.find((value) => value.endsWith("_asc")) ||
                matches[0]
            );
        }

        getFiltersIndexName(indexName) {
            const activeCurrency = this.getActiveCurrency();
            const currencyIndex = this.getCurrencyReplicaIndex(
                activeCurrency,
                this.getPriceSortDirection(indexName)
            );
            return currencyIndex || indexName || this.getLastIndexName();
        }

        // ---- Networking helpers ----
        getQueryUrl() {
            const url = new URL(`//${this.algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`, window.location.origin);
            url.searchParams.set("x-algolia-api-key", this.algoliaApiKey);
            url.searchParams.set("x-algolia-application-id", this.algoliaAppId);
            return url;
        }

        getDefaultProductsIndex() {
            const sharedDefaultIndex = window.poAlgoliaUtils?.getDefaultProductsIndex?.();
            if (sharedDefaultIndex) {
                return sharedDefaultIndex;
            }

            const cfg = window.algoliaShopify?.config;
            const market = window.poAlgoliaUtils?.getCurrentMarket?.();
            const normalizedSuffix =
                window.poAlgoliaUtils?.normalizeMarketSuffix?.(market?.suffix) || market?.suffix;

            if (cfg?.index_prefix) {
                return `${cfg.index_prefix}products${normalizedSuffix ? `_${normalizedSuffix}` : ""}`;
            }

            return "shopify_products";
        }

        getLastIndexName() {
            const resolvedIndex = (
                window.__poPlpCurrentIndexName ||
                this.loadMoreIndex ||
                window.poAlgoliaUtils?.getLastIndexName?.(null) ||
                window.algoliaShopify?.instantsearch?.search?.helper?.state?.index ||
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index ||
                this.getDefaultProductsIndex()
            );

            return window.poAlgoliaUtils?.normalizeIndexName?.(resolvedIndex) || resolvedIndex;
        }

        getResultsScrollTarget() {
            return (
                document.querySelector(".po-custom-algolia-header") ||
                document.getElementById("algolia-shopify-instantsearch") ||
                document.querySelector(".ais-page")
            );
        }

        getResultsScrollOffset() {
            const header = document.querySelector(".js-headerGroup") ||
                document.getElementById("po-main-header-fixed") ||
                document.querySelector("sticky-header");
            const announcement = document.querySelector("sticky-announcement");
            const spacing = window.innerWidth < 768 ? 16 : 24;

            return (header?.offsetHeight || 0) + (announcement?.offsetHeight || 0) + spacing;
        }

        scrollToResultsStart() {
            const target = this.getResultsScrollTarget();

            if (!target) {
                return;
            }

            const targetTop = Math.max(
                0,
                Math.floor(window.scrollY + target.getBoundingClientRect().top - this.getResultsScrollOffset())
            );

            if (window.scrollY <= targetTop + 8) {
                return;
            }

            window.scrollTo({
                top: targetTop,
                behavior: "auto",
            });
        }

        requestScrollToResultsStart() {
            if (this.pendingResultsScrollFrame !== null) {
                window.cancelAnimationFrame(this.pendingResultsScrollFrame);
            }

            this.pendingResultsScrollFrame = window.requestAnimationFrame(() => {
                this.pendingResultsScrollFrame = null;
                this.scrollToResultsStart();
            });
        }

        loadFilteredProducts(facets, facetFilters, indexName) {
            const url = this.getQueryUrl();
            const params = new URLSearchParams(this.fetchParams);
            const normalizedFacetFilters = this.normalizeFacetFilters(facetFilters);
            const resolvedIndexName = indexName || this.getLastIndexName();

            this.abortPendingFacetFetch();
            this.abortPendingResultCountFetch();

            if (normalizedFacetFilters.length > 0) {
                params.set("facetFilters", JSON.stringify(normalizedFacetFilters));
            } else {
                params.delete("facetFilters");
            }

            params.set("page", "0");

            if (facets?.length > 0) {
                params.set("facets", JSON.stringify(facets));
            } else {
                params.delete("facets");
            }
            applyMinimalHitPayloadToParams(params, {
                includePriceData: this.isPriceSortIndex(resolvedIndexName),
            });

            const req = {
                indexName: resolvedIndexName,
                params: params.toString(),
            }

            poPlpSortDebug("loadFilteredProducts.request", {
                indexName: req.indexName,
                facetFilters: normalizedFacetFilters,
                params: getSortDebugParamsSummary(req.params),
            });

            window.__poPlpCurrentIndexName = req.indexName;
            this.fetchParams = req.params;
            this.loadMoreIndex = req.indexName;
            this.activeFacetFilters = normalizedFacetFilters;

            this.requestScrollToResultsStart();
            this.showLoader();
            this.resetBanners();
            this.firstPageRendered = false;
            const renderToken = this.prepareProductRender();

            return fetch(url, {
                body: JSON.stringify({requests: [req]}),
                method: "POST",
            })
                .then((response) => response.json())
                .then(async (data) => {
                    if (renderToken !== this.productRenderToken) return;
                    const result = data.results?.[0];
                    const hitsListEl = document.querySelector(".ais-Hits-list");

                    if (!result) {
                        console.error("No results found for the given query.");
                        this.showNoResults();
                        return;
                    }

                    if (!hitsListEl) {
                        console.error("Hits list element not found. Result data:", result);
                        return;
                    }

                    if (Array.isArray(result?.hits) && this.isPriceSortIndex(req.indexName)) {
                        result.hits = this.sortHitsForPriceIndex(result.hits, req.indexName);
                    }
                    this.cachePlpFullHits(result.hits || []);

                    hitsListEl.replaceChildren();

                    const totalHits = Number(result.nbHits) || 0;
                    const resultCountCacheKey = this.getFacetCacheKey(
                        normalizedFacetFilters,
                        req.indexName,
                        this.getCurrentSearchQuery()
                    );
                    this.lastMaxHits = totalHits;
                    this.productHits = totalHits;
                    this.displayProductHits = Number(this.filteredResultCountMap.get(resultCountCacheKey));
                    if (!Number.isFinite(this.displayProductHits)) {
                        this.displayProductHits = totalHits;
                    }
                    this.totalHits = this.getDisplayProductHits();
                    this.hasResultStats = true;
                    if (this.template !== "search" && !this.isCollectionUngrouped()) {
                        this.setCachedResultCount(resultCountCacheKey, totalHits);
                    }

                    this.syncVisibleResultCounts();
                    this.updateTabsAndOptionsVisibility();
                    document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                        detail: { nbHits: this.getDisplayProductHits() }
                    }));

                    if (this.template === "search") {
                        this.syncSearchDisplayProductCount({
                            params: req.params,
                            indexName: req.indexName,
                            facetFilters: normalizedFacetFilters,
                            query: this.getCurrentSearchQuery(),
                        });
                    } else if (this.isCollectionUngrouped()) {
                        this.syncUngroupedCollectionDisplayCount({
                            params: req.params,
                            indexName: req.indexName,
                            facetFilters: normalizedFacetFilters,
                        });
                    }

                    poPlpSortDebug("loadFilteredProducts.response", {
                        indexName: req.indexName,
                        nbHits: totalHits,
                        nbPages: result.nbPages,
                        firstHits: (result.hits || []).slice(0, 10).map(getSortDebugHitSummary),
                        firstHitLines: (result.hits || []).slice(0, 10).map(getSortDebugHitLine),
                        groupKeys: this.groupKeys.slice(),
                    });

                    await this.appendProductHitsInBatches(hitsListEl, result.hits, { renderToken });
                    if (renderToken !== this.productRenderToken) return;
                    document.dispatchEvent(
                        new CustomEvent("plp:page-batch-appended", {
                            detail: {
                                fromIndex: 0,
                                batchSize: Math.min(
                                    Array.isArray(result.hits) ? result.hits.length : 0,
                                    Math.max(1, Number(this.productsPerPage) || 0)
                                ),
                            },
                        })
                    );
                    if (this.template === "search") {
                        this.searchLoadMoreOffset = Array.isArray(result.hits)
                            ? result.hits.length
                            : 0;
                    }

                    this.maxPages = result.nbPages;
                    this.lastNbHits = Math.min(result.nbHits, this.getProductsPerPage());

                    if (result.nbHits === 0) {
                        this.showNoResults();
                    } else if (this.groupKeys.length > 0 && this.shouldLoadProductGroupsForIndex(req.indexName, normalizedFacetFilters)) {
                        this.loadProductGroups(this.groupKeys, req.indexName);
                    } else {
                        this.dispatchCurrentFacetsLoaded(result);
                        this.onCardsLoaded(renderToken);
                    }

                    document.dispatchEvent(new CustomEvent("plp:filters-hits", {detail: this.groupKeys}));
                    this.sendTealiumData(result);
                })
                .catch(error => {
                    if (renderToken === this.productRenderToken) {
                        console.error('Error fetching Algolia data:', error);
                    }
                });
        }

        loadFilteredFacets(facets, facetFilters, indexName, options = {}) {
            const url = this.getQueryUrl();
            const requests = [];
            const requestedIndex = indexName || this.getLastIndexName();
            const facetsIndex = this.getFiltersIndexName(requestedIndex);
            const cacheKey = this.getFacetCacheKey(facetFilters, requestedIndex);
            const cachedResults = this.filteredFacetsMap.get(cacheKey);
            const syncResultCount = options?.syncResultCount === true;
            const controller = new AbortController();

            if (cachedResults !== undefined || (facetFilters && facetFilters.length === 0)) {
                this.abortPendingFacetFetch();
                this.dispatchFacetsUpdated(cachedResults || [], true);

                if (syncResultCount) {
                    if (this.template === "search") {
                        const params = new URLSearchParams(this.fetchParams);
                        if (facets && facets.length > 0) {
                            params.set("facets", JSON.stringify(facets));
                        } else {
                            params.delete("facets");
                        }
                        params.set("facetFilters", JSON.stringify(facetFilters || []));
                        params.set("hitsPerPage", "0");
                        this.syncSearchDisplayProductCount({
                            params: params.toString(),
                            indexName: facetsIndex,
                            facetFilters,
                            query: this.getCurrentSearchQuery(),
                        });
                    } else if (this.isCollectionUngrouped()) {
                        const params = new URLSearchParams(this.fetchParams);
                        if (facets && facets.length > 0) {
                            params.set("facets", JSON.stringify(facets));
                        } else {
                            params.delete("facets");
                        }
                        params.set("facetFilters", JSON.stringify(facetFilters || []));
                        this.syncUngroupedCollectionDisplayCount({
                            params: params.toString(),
                            indexName: facetsIndex,
                            facetFilters,
                        });
                    } else {
                        const cachedCount = Number(cachedResults?.[0]?.nbHits);
                        if (Number.isFinite(cachedCount)) {
                            document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                                detail: {nbHits: cachedCount}
                            }));
                            this.setCachedResultCount(cacheKey, cachedCount);
                        }
                    }
                }

                return;
            }

            this.abortPendingFacetFetch();
            this.pendingFacetController = controller;

            for (let filter of facetFilters) {
                const params = new URLSearchParams(this.fetchParams);

                if (facets && facets.length > 0) {
                    params.set("facets", JSON.stringify(facets));
                } else {
                    params.delete("facets");
                }

                params.set("facetFilters", JSON.stringify([filter]));
                params.set("hitsPerPage", "0");

                requests.push({
                    indexName: facetsIndex,
                    params: params.toString()
                });
            }

            fetch(url, {
                body: JSON.stringify({requests}),
                method: "POST",
                signal: controller.signal,
            })
                .then(response => response.json())
                .then(data => {
                    if (this.pendingFacetController !== controller) {
                        return;
                    }

                    if (!data.results) {
                        console.warn("No results found for filtered facets. Req:", requests);
                        return;
                    }

                    this.dispatchFacetsUpdated(data.results, false);
                    this.setCachedFacetResults(cacheKey, data.results);

                    if (syncResultCount && data.results.length === 1) {
                        if (this.template === "search") {
                            this.syncSearchDisplayProductCount({
                                params: requests[0]?.params,
                                indexName: facetsIndex,
                                facetFilters,
                                query: this.getCurrentSearchQuery(),
                                useCached: false,
                            });
                        } else if (this.isCollectionUngrouped()) {
                            this.syncUngroupedCollectionDisplayCount({
                                params: requests[0]?.params,
                                indexName: facetsIndex,
                                facetFilters,
                                useCached: false,
                            });
                        } else {
                            const nbHits = Number(data.results[0]?.nbHits) || 0;
                            document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {detail: {nbHits}}));
                            this.setCachedResultCount(cacheKey, nbHits);
                        }
                    }
                })
                .catch(error => {
                    if (!this.isAbortError(error)) {
                        console.error('Error fetching Algolia data:', error);
                    }
                })
                .finally(() => {
                    if (this.pendingFacetController === controller) {
                        this.pendingFacetController = null;
                    }
                });
        }

        loadFilteredResultCount(facets, facetFilters, indexName) {
            const url = this.getQueryUrl();
            const requestedIndex = indexName || this.getLastIndexName();
            const facetsIndex = this.getFiltersIndexName(requestedIndex);
            const cacheKey = this.getFacetCacheKey(facetFilters || [], requestedIndex);
            const cachedResults = this.filteredResultCountMap.get(cacheKey);
            const controller = new AbortController();
            const params = new URLSearchParams(this.fetchParams);

            params.set("facets", JSON.stringify(facets || []));
            params.set("facetFilters", JSON.stringify(facetFilters || []));
            params.set("hitsPerPage", "0");

            if (this.isCollectionUngrouped()) {
                this.syncUngroupedCollectionDisplayCount({
                    params: params.toString(),
                    indexName: facetsIndex,
                    facetFilters,
                });
                return;
            }

            if (cachedResults !== undefined && cachedResults !== null) {
                this.abortPendingResultCountFetch();
                document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {detail: {nbHits: cachedResults}}));
                return;
            }

            this.abortPendingResultCountFetch();
            this.pendingResultCountController = controller;

            const req = {
                indexName: facetsIndex,
                params: params.toString(),
            }

            if (this.template === "search") {
                this.syncSearchDisplayProductCount({
                    params: req.params,
                    indexName: facetsIndex,
                    facetFilters,
                    query: this.getCurrentSearchQuery(),
                    useCached: false,
                });
                return;
            }

            fetch(url, {
                body: JSON.stringify({requests: [req]}),
                method: "POST",
                signal: controller.signal,
            })
                .then(response => response.json())
                .then(data => {
                    if (this.pendingResultCountController !== controller) {
                        return;
                    }

                    const nbHits = data.results[0]?.nbHits || 0;
                    document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {detail: {nbHits}}));
                    this.setCachedResultCount(cacheKey, nbHits);
                })
                .catch(error => {
                    if (!this.isAbortError(error)) {
                        console.error('Error fetching Algolia data:', error);
                    }
                })
                .finally(() => {
                    if (this.pendingResultCountController === controller) {
                        this.pendingResultCountController = null;
                    }
                });
        }

        /**
         * Builds the query that ranks a group as Algolia does for its x_groupkey filter alone.
         * Collection filters and rule contexts are left out on purpose: they trigger the
         * collection merchandising rules, whose pins and boosts must not reorder card variants.
         *
         * @param {string} groupKey - The `meta.custom.x_groupkey` value of the group.
         * @returns {URLSearchParams} Query params for the group ranking request.
         */
        buildGroupRankingParams(groupKey) {
            return new URLSearchParams({
                facetFilters: JSON.stringify([`meta.custom.x_groupkey:${groupKey}`]),
                page: "0",
                hitsPerPage: "200",
                distinct: "0",
                analytics: "false",
                clickAnalytics: "false",
                attributesToRetrieve: GROUP_RANKING_HIT_ATTRIBUTES,
                attributesToHighlight: EMPTY_HIT_ATTRIBUTES,
                attributesToSnippet: EMPTY_HIT_ATTRIBUTES,
            });
        }

        /**
         * Sorts the collection-scoped group hits by the x_groupkey-only group ranking.
         * Hits missing from the ranking keep their relative order after the ranked ones.
         *
         * @param {Array<Object>} scopedHits - Hits returned by the collection-scoped group query.
         * @param {Array<Object>} rankingHits - Hits returned by the x_groupkey-only group query.
         * @returns {Array<Object>} The scoped hits in group ranking order.
         */
        orderGroupHitsByRanking(scopedHits = [], rankingHits = []) {
            if (!Array.isArray(scopedHits)) {
                return [];
            }

            if (!Array.isArray(rankingHits) || rankingHits.length === 0) {
                return scopedHits;
            }

            const rankByObjectId = new Map();

            rankingHits.forEach((hit, index) => {
                const objectId = String(hit?.objectID ?? "");

                if (objectId && !rankByObjectId.has(objectId)) {
                    rankByObjectId.set(objectId, index);
                }
            });

            return scopedHits
                .map((hit, originalIndex) => ({
                    hit,
                    originalIndex,
                    rank: rankByObjectId.get(String(hit?.objectID ?? "")) ?? Number.POSITIVE_INFINITY,
                }))
                .sort((left, right) => (
                    left.rank === right.rank
                        ? left.originalIndex - right.originalIndex
                        : left.rank - right.rank
                ))
                .map(({ hit }) => hit);
        }

        /**
         * Loads the color variants of each PLP group and caches them in group ranking order.
         * Each group sends a collection-scoped query, which decides which variants belong to the card,
         * and an x_groupkey-only query, which decides their order.
         *
         * @param {Array<string>} xGroupKeys - Group keys rendered in the current PLP page.
         * @param {string|null} indexName - Algolia index used by the current PLP sort.
         */
        loadProductGroups(xGroupKeys = [], indexName = null) {
            const url = this.getQueryUrl();

            if (!url) {
                console.error("Invalid URL for Algolia queries.");
                return;
            }

            const baseParams = this.fetchParams
                ? new URLSearchParams(this.fetchParams)
                : new URLSearchParams();
            const resolvedFacetFilters = this.normalizeFacetFilters(this.activeFacetFilters);
            const fallbackFacetFilters = this.normalizeFacetFilters(baseParams.get("facetFilters"));
            const sharedFacetFilters = resolvedFacetFilters.length ? resolvedFacetFilters : fallbackFacetFilters;
            const groupIndexName = indexName || this.getLastIndexName();
            const inheritedRuleContexts = this.extractRuleContextsFromParams(baseParams);
            const sharedFilters = this.getScopedGroupFilters(baseParams.get("filters") || "");
            const controller = new AbortController();

            this.abortPendingGroupFetch();
            this.pendingGroupController = controller;

            const requests = xGroupKeys.flatMap((key) => {
                const mergedFilters = sharedFacetFilters.length
                    ? [...sharedFacetFilters, `meta.custom.x_groupkey:${key}`]
                    : [`meta.custom.x_groupkey:${key}`];

                const params = new URLSearchParams({
                    facetFilters: JSON.stringify(mergedFilters),
                    facets: JSON.stringify(["meta.custom.x_groupkey"]),
                });

                params.set("page", "0");
                params.set("hitsPerPage", "200");
                params.set("distinct", "0");
                params.set("clickAnalytics", "true");
                if (sharedFilters) {
                    params.set("filters", sharedFilters);
                }
                if (inheritedRuleContexts.length) {
                    params.set("ruleContexts", JSON.stringify(inheritedRuleContexts));
                }
                if (this.shouldUseCompactGroupHits(groupIndexName)) {
                    applyMinimalGroupPayloadToParams(params, {
                        includePriceData: this.isPriceSortIndex(groupIndexName),
                    });
                } else {
                    applyMinimalHitPayloadToParams(params, {
                        includePriceData: this.isPriceSortIndex(groupIndexName),
                    });
                }

                return [
                    {
                        indexName: groupIndexName,
                        params: params.toString(),
                    },
                    {
                        indexName: groupIndexName,
                        params: this.buildGroupRankingParams(key).toString(),
                    },
                ];
            });
            const requestBatches = [];

            for (let index = 0; index < requests.length; index += ALGOLIA_MAX_QUERIES_PER_BATCH) {
                requestBatches.push(requests.slice(index, index + ALGOLIA_MAX_QUERIES_PER_BATCH));
            }

            poPlpSortDebug("loadProductGroups.request", {
                indexName: groupIndexName,
                groupKeys: xGroupKeys,
                sharedFacetFilters,
                sharedFilters,
                inheritedRuleContexts,
                compactPayload: this.shouldUseCompactGroupHits(groupIndexName),
                requests: requests.map((request) => ({
                    indexName: request.indexName,
                    params: getSortDebugParamsSummary(request.params),
                })),
            });

            Promise.all(requestBatches.map((batchRequests) => fetch(url, {
                body: JSON.stringify({requests: batchRequests}),
                method: "POST",
                signal: controller.signal,
            }).then(response => response.json())))
                .then(batchResponses => {
                    if (this.pendingGroupController !== controller) {
                        return;
                    }

                    const invalidResponse = batchResponses.find((data) => !Array.isArray(data?.results));

                    if (invalidResponse) {
                        console.warn("[po-groups] Algolia response non valida:", JSON.stringify(invalidResponse).slice(0, 500));
                        return;
                    }

                    const results = batchResponses.flatMap((data) => data.results);
                    const groups = [];

                    xGroupKeys.forEach((_key, keyIndex) => {
                        const scopedHits = results[keyIndex * 2]?.hits || [];

                        if (scopedHits.length === 0) {
                            return;
                        }

                        const groupHits = this.orderGroupHitsByRanking(
                            scopedHits,
                            results[keyIndex * 2 + 1]?.hits || []
                        );
                        this.cachePlpFullHits(groupHits);
                        groups.push({
                            xGroupKey: groupHits[0].meta?.custom?.x_groupkey,
                            hits: this.filterHits(groupHits, groupIndexName),
                        });
                    });

                    const sortedGroups = this.sortGroupsForPriceIndex(groups, groupIndexName);

                    poPlpSortDebug("loadProductGroups.response", {
                        indexName: groupIndexName,
                        groups: sortedGroups.map((group) => ({
                            xGroupKey: group.xGroupKey,
                            hits: (group.hits || []).map(getSortDebugHitSummary),
                            hitLines: (group.hits || []).map(getSortDebugHitLine),
                        })),
                    });

                    const cachedGroups = this.setCachedPlpGroups(sortedGroups);
                    this.syncRenderedProductCardHandles(cachedGroups, groupIndexName);
                    this.syncRenderedProductCardVariantOrder(cachedGroups);
                    this.syncRenderedGroupPrices(groupIndexName);
                    document.dispatchEvent(new CustomEvent("plp:groups-loaded", {detail: cachedGroups}));
                    if (this.facets.length === 0) {
                        this.facets = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.facets || [];
                    }
                    this.dispatchCurrentFacetsLoaded(results[0] || null);

                    this.onCardsLoaded(this.productRenderToken);
                })
                .catch(error => {
                    if (!this.isAbortError(error)) {
                        console.error('Error fetching Algolia data:', error);
                    }
                })
                .finally(() => {
                    if (this.pendingGroupController === controller) {
                        this.pendingGroupController = null;
                    }
                });
        }

        filterHits(hits = [], indexName = this.getLastIndexName()) {
            const seenVariantKeys = new Set();
            const filteredHits = [];
            const compactHits = this.shouldUseCompactGroupHits(indexName);
            const rxFilteredHits = window.poPlpRxUtils?.filterNonRxHits(hits) ?? hits;

            for (const hit of rxFilteredHits) {
                const variantKey = this.getHitColorKey(hit);

                if (!variantKey || seenVariantKeys.has(variantKey)) {
                    continue;
                }

                filteredHits.push(compactHits
                    ? { handle: hit.handle }
                    : {
                        handle: hit.handle,
                        id: hit.id,
                        image: hit.image,
                        inventory_available: hit.inventory_available,
                        objectID: hit.objectID,
                        title: hit.title,
                        collections: hit.collections || [],
                        meta: hit.meta,
                        price: hit.price,
                        compare_at_price: hit.compare_at_price,
                        price_range: hit.price_range,
                        variants_min_price: hit.variants_min_price,
                        variants_max_price: hit.variants_max_price,
                        market_pricing_usd: hit.market_pricing_usd,
                        market_pricing_eur: hit.market_pricing_eur,
                        market_pricing_cad: hit.market_pricing_cad,
                        market_pricing_jpy: hit.market_pricing_jpy,
                        market_pricing_gbp: hit.market_pricing_gbp,
                    });

                seenVariantKeys.add(variantKey);
            }

            return filteredHits;
        }

        dispatchFacetsUpdated(results, cached) {
            document.dispatchEvent(new CustomEvent("plp:facets-updated", {
                detail: {results, cached}
            }));
        }

        showAisPage() {
            const aisPage = document.querySelector(".ais-page");
            const opPulsingLogo = this.template === "collection"
                ? null
                : document.querySelector(".po-pulsing-logo");
            const hasSearchArticles = this.template === "search"
                && this.getActiveArticleCount() > 0;

            if (opPulsingLogo) {
                opPulsingLogo.toggleAttribute("data-loaded", true);

                if (this.lastNbHits > 0 || hasSearchArticles) {
                    opPulsingLogo.style.display = "none";
                }
            }

            if (aisPage) {
                aisPage.style.display = "block";
            }

            this.setGridSkeletonVisibility(false);
        }

        showLoader() {
            const aisPage = document.querySelector(".ais-page");
            const opPulsingLogo = this.template === "collection"
                ? null
                : document.querySelector(".po-pulsing-logo");

            if (opPulsingLogo) {
                opPulsingLogo.toggleAttribute("data-loaded", false);
                opPulsingLogo.style.removeProperty("display");
            }

            if (aisPage) {
                aisPage.style.display = this.template === "collection" ? "block" : "none";
                aisPage.classList.remove("ais-Hits--empty");
                aisPage.querySelector(".ais-hit-empty")?.remove();
                aisPage.querySelector(".ais-Hits-list")?.style.removeProperty("display");
            }

            this.setGridSkeletonVisibility(true);
        }

        showNoResults() {
            const aisHits = document.querySelector(".ais-page .ais-Hits");
            const translations = getAlgoliaTranslations();
            const container = document.querySelector(".ais-hit-empty") || document.createElement("div");
            const hitList = document.querySelector(".ais-Hits-list");

            this.setGridSkeletonVisibility(false);

            if (aisHits && translations) {
                aisHits.classList.add("ais-Hits--empty");

                container.innerHTML = `
                    <div class="ais-hit-empty">
                        <div class="ais-hit-empty--title tw-font-heading">
                          ${translations.noResultFound}
                        </div>
                        <div class="ais-hit-empty--clear tw-font-heading">
                          ${translations.try} <a class="ais-hit-empty--clear-filters ais-link tw-font-heading">
                            ${translations.clearFilters}
                          </a> ${translations.or} <a class="ais-hit-empty--clear-input ais-link tw-font-heading">
                            ${translations.changeInput}
                          </a>
                        </div>
                    </div>
                `;

                container.querySelector(".ais-hit-empty--clear-filters")?.addEventListener("click", () => {
                    document.dispatchEvent(new CustomEvent("plp:clear-filters"));
                });

                container.querySelector(".ais-hit-empty--clear-input")?.addEventListener("click", () => {
                    document.dispatchEvent(new CustomEvent("plp:open-filters"));
                });

                aisHits.append(container);
            }

            if (hitList) hitList.style.display = "none";

            this.showAisPage();
        }

        normalizeLower(value) {
            return (value || "").toString().trim().toLowerCase();
        }

        resetArticlesState() {
            if (this.template !== "search") return;

            this.articlesLoaded = false;
            this._articlesLoadedForQuery = "";
            this.articleCount = 0;

            document
                .querySelectorAll(".po-articles-count")
                .forEach((el) => {
                    el.textContent = "0";
                });

            const listEl = document.querySelector("#po-tab-panel-articles .po-articles-list");
            if (listEl) {
                listEl.innerHTML = "";
            }
        }

        getDisplayProductHits() {
            const rawProductHits = Math.max(0, Number(this.productHits || 0));
            const displayHits = Number(this.displayProductHits);

            if (this.template !== "search") {
                if (this.isCollectionUngrouped() && Number.isFinite(displayHits)) {
                    return Math.max(0, displayHits);
                }

                return rawProductHits;
            }

            return Number.isFinite(displayHits)
                ? Math.max(0, displayHits)
                : rawProductHits;
        }

        getSearchDisplayCountParams(params = this.fetchParams) {
            const nextParams = new URLSearchParams(params || "");
            nextParams.set("hitsPerPage", "0");
            nextParams.set("page", "0");
            nextParams.delete("offset");
            nextParams.delete("length");
            nextParams.delete("clickAnalytics");
            return nextParams;
        }

        getCollectionDisplayCountParams(params = this.fetchParams) {
            const nextParams = new URLSearchParams(params || "");
            nextParams.set("hitsPerPage", "250");
            nextParams.set("page", "0");
            nextParams.set("distinct", "0");
            nextParams.delete("offset");
            nextParams.delete("length");
            nextParams.delete("clickAnalytics");
            return nextParams;
        }

        async syncUngroupedCollectionDisplayCount({
            params = this.fetchParams,
            indexName = this.getLastIndexName(),
            facetFilters = this.activeFacetFilters,
            useCached = true,
        } = {}) {
            if (!this.isCollectionUngrouped()) {
                return null;
            }

            const requestedIndex = indexName || this.getLastIndexName();
            const normalizedFacetFilters = this.normalizeFacetFilters(facetFilters);
            const cacheKey = this.getFacetCacheKey(normalizedFacetFilters, requestedIndex);
            const cachedCount = this.filteredResultCountMap.get(cacheKey);

            if (useCached && cachedCount !== undefined && cachedCount !== null) {
                const nextDisplayCount = Math.max(0, Number(cachedCount || 0));
                this.displayProductHits = nextDisplayCount;
                this.totalHits = nextDisplayCount;
                this.updateTotalResultsCount(nextDisplayCount);
                this.updateTabsAndOptionsVisibility();
                document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                    detail: { nbHits: nextDisplayCount }
                }));
                return nextDisplayCount;
            }

            const url = this.getQueryUrl();
            if (!url) {
                return null;
            }

            const controller = new AbortController();
            this.abortPendingResultCountFetch();
            this.pendingResultCountController = controller;

            const baseParams = this.getCollectionDisplayCountParams(params);
            applyMinimalHitPayloadToParams(baseParams);

            const batchSize = 4;
            let page = 0;
            let totalPages = 1;
            const colorKeys = new Set();

            try {
                while (page < totalPages) {
                    const requests = [];

                    for (let batchPage = page; batchPage < Math.min(page + batchSize, totalPages); batchPage += 1) {
                        const pageParams = new URLSearchParams(baseParams);
                        pageParams.set("page", String(batchPage));
                        requests.push({
                            indexName: this.getFiltersIndexName(requestedIndex),
                            params: pageParams.toString(),
                        });
                    }

                    const response = await fetch(url, {
                        body: JSON.stringify({ requests }),
                        method: "POST",
                        signal: controller.signal,
                    });
                    const data = await response.json();

                    if (this.pendingResultCountController !== controller) {
                        return null;
                    }

                    const results = Array.isArray(data?.results) ? data.results : [];

                    if (page === 0) {
                        totalPages = Math.max(1, Number(results?.[0]?.nbPages) || 1);
                    }

                    results.forEach((result) => {
                        (result?.hits || []).forEach((hit) => {
                            const colorKey = this.getHitColorKey(hit);
                            if (colorKey) {
                                colorKeys.add(colorKey);
                            }
                        });
                    });

                    if (results.length === 0) {
                        break;
                    }

                    page += requests.length;
                }

                const uniqueCount = colorKeys.size;
                this.setCachedResultCount(cacheKey, uniqueCount);

                if (this.isCollectionUngrouped()) {
                    this.displayProductHits = uniqueCount;
                    this.totalHits = uniqueCount;
                    this.updateTotalResultsCount(uniqueCount);
                    this.updateTabsAndOptionsVisibility();
                }

                document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                    detail: { nbHits: uniqueCount }
                }));

                return uniqueCount;
            } catch (error) {
                if (!this.isAbortError(error)) {
                    console.error("Error fetching Algolia ungrouped collection count:", error);
                }
                return null;
            } finally {
                if (this.pendingResultCountController === controller) {
                    this.pendingResultCountController = null;
                }
            }
        }

        async syncSearchDisplayProductCount({
            params = this.fetchParams,
            indexName = this.getLastIndexName(),
            facetFilters = this.activeFacetFilters,
            query = this.getCurrentSearchQuery(),
            useCached = true,
        } = {}) {
            if (this.template !== "search") {
                return null;
            }

            const queryKey = this.getSearchQueryKey(query);
            if (!queryKey) {
                return null;
            }

            const requestedIndex = indexName || this.getLastIndexName();
            const normalizedFacetFilters = this.normalizeFacetFilters(facetFilters);
            const cacheKey = this.getFacetCacheKey(normalizedFacetFilters, requestedIndex, query);
            const cachedCount = this.filteredResultCountMap.get(cacheKey);

            if (useCached && cachedCount !== undefined && cachedCount !== null) {
                const nextDisplayCount = Math.max(0, Number(cachedCount || 0));
                this.displayProductHits = nextDisplayCount;
                this.syncSearchResultCounts(query);
                this.updateTabsAndOptionsVisibility();
                document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                    detail: { nbHits: nextDisplayCount }
                }));
                return nextDisplayCount;
            }

            const url = this.getQueryUrl();
            if (!url) {
                return null;
            }

            const controller = new AbortController();
            this.abortPendingResultCountFetch();
            this.pendingResultCountController = controller;

            const req = {
                indexName: this.getFiltersIndexName(requestedIndex),
                params: this.getSearchDisplayCountParams(params).toString(),
            };

            try {
                const response = await fetch(url, {
                    body: JSON.stringify({ requests: [req] }),
                    method: "POST",
                    signal: controller.signal,
                });
                const data = await response.json();

                if (this.pendingResultCountController !== controller) {
                    return null;
                }

                const uniqueCount = Math.max(0, Number(data?.results?.[0]?.nbHits || 0));
                this.setCachedResultCount(cacheKey, uniqueCount);
                const nextDisplayCount = uniqueCount;

                if (this.getSearchQueryKey(this.getCurrentSearchQuery()) === queryKey) {
                    this.displayProductHits = nextDisplayCount;
                    this.syncSearchResultCounts(query);
                    this.updateTabsAndOptionsVisibility();
                }

                document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                    detail: { nbHits: nextDisplayCount }
                }));

                return nextDisplayCount;
            } catch (error) {
                if (!this.isAbortError(error)) {
                    console.error("Error fetching Algolia result count:", error);
                }
                return null;
            } finally {
                if (this.pendingResultCountController === controller) {
                    this.pendingResultCountController = null;
                }
            }
        }

        updateTotalResultsCount(totalHits = this.totalHits) {
            const productCount = Math.max(0, Number(totalHits || 0));
            const totalResults = productCount;

            document
                .querySelectorAll(".po-total-results-quantity")
                .forEach((el) => {
                    el.textContent = totalResults;
                    el.closest(".po-total-results-wrapper")?.classList?.remove("tw-hidden");
                });
        }

        syncVisibleResultCounts(query = this.getCurrentSearchQuery()) {
            if (!this.hasResultStats) return;

            if (this.template === "search") {
                this.syncSearchResultCounts(query);
                return;
            }

            this.updateTotalResultsCount(this.getDisplayProductHits());
        }

        getStoredSearchContext() {
            try {
                return JSON.parse(sessionStorage.getItem("po-search-context") || "{}");
            } catch (e) {
                return {};
            }
        }

        normalizeSearchQuery(value) {
            return (value == null ? "" : String(value)).trim();
        }

        getSearchQueryFromUrl() {
            if (typeof window === "undefined") {
                return "";
            }

            try {
                return this.normalizeSearchQuery(new URLSearchParams(window.location.search).get("q"));
            } catch (e) {
                return "";
            }
        }

        getCurrentSearchQuery(preferredQuery = "") {
            const candidates = [
                preferredQuery,
                window.algoliaShopify?.instantsearch?.search?.helper?.state?.query,
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.query,
            ];

            if (this.template === "search") {
                candidates.push(this.getSearchQueryFromUrl(), this.initialSearchInput);
            }

            for (const candidate of candidates) {
                const normalized = this.normalizeSearchQuery(candidate);
                if (normalized) {
                    return normalized;
                }
            }

            return "";
        }

        getSearchQueryKey(query = this.getCurrentSearchQuery()) {
            return this.normalizeLower(query);
        }

        getActiveArticleCount(query = this.getCurrentSearchQuery()) {
            if (this.template !== "search") {
                return Number(this.articleCount || 0);
            }

            const queryKey = this.getSearchQueryKey(query);
            return this._articlesLoadedForQuery === queryKey
                ? Number(this.articleCount || 0)
                : 0;
        }

        syncSearchResultCounts(query = this.getCurrentSearchQuery()) {
            if (this.template !== "search") return;

            const productCount = this.getDisplayProductHits();
            const articleCount = this.getActiveArticleCount(query);
            const totalCount = productCount + articleCount;

            this.totalHits = totalCount;

            document
                .querySelectorAll(".po-total-results-quantity")
                .forEach((el) => {
                    el.textContent = totalCount;
                });

            document
                .querySelectorAll(".po-total-products-quantity")
                .forEach((el) => {
                    el.textContent = productCount;
                });

            const span = document.querySelector(".po-articles-count");
            if (span) span.textContent = articleCount;
        }

        getSerpCounts(productHits = this.getDisplayProductHits(), articleCount = this.getActiveArticleCount()) {
            const supportCount = Math.max(0, Number(articleCount || 0));
            const productCount = Math.max(0, Number(productHits || 0));
            return { productCount, supportCount };
        }

        getSerpPageDesign(productCount, supportCount) {
            if (productCount === 0 && supportCount === 0) return "Empty";
            if (productCount === 0 && supportCount > 0) return "Pages";
            return "Tiles&Pages";
        }

        getSearchResultItemsQnt(productCount, supportCount) {
            return productCount === 0 && supportCount > 0 ? "Static" : String(productCount);
        }

        cloneTealiumPayload(requestedData = {}) {
            if (!requestedData || typeof requestedData !== "object" || Array.isArray(requestedData)) {
                return {};
            }

            try {
                return JSON.parse(JSON.stringify(requestedData));
            } catch (_error) {
                return { ...requestedData };
            }
        }

        cloneBaseTealiumPayload() {
            let basePayload = {};

            try {
                if (typeof tealiumData !== "undefined" && tealiumData && typeof tealiumData === "object") {
                    basePayload = tealiumData;
                }
            } catch (_error) {
                // no-op
            }

            return this.cloneTealiumPayload(basePayload);
        }

        getBaseTealiumPayload() {
            const payload = this.cloneBaseTealiumPayload();

            payload.Page_Country = payload.Page_Country || this.getActiveCountry() || "";
            payload.Order_Currency = payload.Order_Currency || this.getActiveCurrency() || "";
            payload.User_LoginStatus = payload.User_LoginStatus || (payload.User_Id ? "Logged" : "Guest");

            return payload;
        }

        getBaseSerpTealiumPayload() {
            const payload = this.getBaseTealiumPayload();

            delete payload.Products;
            delete payload.Page_Design;
            delete payload.Search_ResultItemsQnt;
            delete payload.Search_FacetValues_String;
            delete payload.Search_Keyword;
            delete payload.Search_KeyActual;
            delete payload.Search_Type;
            delete payload.Search_View;
            delete payload.Events_SearchFiltering;
            delete payload.Events_SearchRun;

            payload.id = "VirtualPage-View";
            payload.Page_Type = "Search";
            payload.Page_Section1 = "Search";
            payload.Page_Country = payload.Page_Country || this.getActiveCountry() || "";
            payload.Order_Currency = payload.Order_Currency || this.getActiveCurrency() || "";
            payload.User_LoginStatus = payload.User_LoginStatus || (payload.User_Id ? "Logged" : "Guest");

            return payload;
        }

        formatSearchTealiumMinorUnitPrice(value) {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? (numericValue / 100).toFixed(2) : "";
        }

        getFirstSearchUsablePrice(...values) {
            for (const value of values) {
                const normalizedValue = String(value || "").trim();
                if (!normalizedValue) {
                    continue;
                }

                const numericValue = Number(normalizedValue);
                if (Number.isFinite(numericValue) && numericValue > 0) {
                    return numericValue.toFixed(2);
                }
            }

            return "";
        }

        hasSearchProductsReadyForTracking(products = {}) {
            return Object.values(products || {}).some((product) => {
                const price = String(product?.Price || "").trim();
                const priceFull = String(product?.PriceFull || "").trim();
                return Boolean(price && priceFull);
            });
        }

        normalizeSearchTealiumCategory(value) {
            if (typeof normalizeTealiumCategoryValue === "function") {
                return normalizeTealiumCategoryValue(value);
            }

            const normalizedValue = String(value || "")
                .trim()
                .replace(/[_-]+/g, " ")
                .replace(/\s+/g, " ")
                .toLowerCase();

            const categoryMap = {
                accessories: "ACCESSORIES",
                accessory: "ACCESSORIES",
                optics: "OPTICS",
                optical: "OPTICS",
                eyeglasses: "OPTICS",
                eyeglass: "OPTICS",
                sun: "SUN",
                sunglasses: "SUN",
                sunglass: "SUN",
                afa: "AFA",
                eyewear: "EYEWEAR",
                electronics: "Electronics",
                electronic: "Electronics",
                giftcard: "GIFTCARD",
                "gift card": "GIFTCARD",
                "gift cards": "GIFTCARD",
                "contact lens": "CONTACT LENSES",
                "contact lenses": "CONTACT LENSES",
                vas: "VAS",
                service: "VAS",
                services: "VAS",
            };

            return categoryMap[normalizedValue] || "EYEWEAR";
        }

        getVisibleSearchTrackingItems() {
            return Array.from(document.querySelectorAll(".ais-Hits-list > .ais-Hits-item")).filter((item) => {
                return item && !item.hidden && item.getAttribute("aria-hidden") !== "true";
            });
        }

        resolveSearchHitUpc(hit = {}, card = null) {
            const candidates = [
                card?.dataset?.upc,
                hit?.upc,
                hit?.barcode,
                hit?.variant_barcode,
                hit?.product_upc,
                hit?.product_barcode,
                card?.dataset?.sku,
                hit?.sku,
                hit?.variant_sku,
                hit?.product_sku,
                hit?.selected_variant?.barcode,
                hit?.selected_variant?.sku,
            ];

            for (const candidate of candidates) {
                const normalizedCandidate = String(candidate || "").trim();
                if (normalizedCandidate) {
                    return normalizedCandidate;
                }
            }

            if (Array.isArray(hit?.variants)) {
                for (const variant of hit.variants) {
                    const variantUpc = String(variant?.barcode || variant?.sku || "").trim();
                    if (variantUpc) {
                        return variantUpc;
                    }
                }
            }

            return "";
        }

        parseSearchTrackingProductData(rawProductData = null) {
            if (!rawProductData) {
                return null;
            }

            if (typeof rawProductData === "string") {
                try {
                    const parsedProductData = JSON.parse(rawProductData);
                    return parsedProductData && typeof parsedProductData === "object"
                        ? parsedProductData
                        : null;
                } catch (_error) {
                    return null;
                }
            }

            return typeof rawProductData === "object"
                ? rawProductData
                : null;
        }

        buildSearchProductsPayload() {
            const products = {};
            const items = this.getVisibleSearchTrackingItems();
            const fullHitsByHandle = this.getCachedPlpFullHitsByHandle();
            const priceSortDirection = this.getPriceSortDirection(this.getLastIndexName());

            items.forEach((item) => {
                const card = item?.querySelector?.(".po-algolia-product-card") || null;
                const tile = item?.querySelector?.(".tw-po-tile") || null;
                const cardSlot = item?.querySelector?.(".po-card-fetch-slot") || null;
                const isCardLoaded = !cardSlot || cardSlot.dataset.cardFetchState === "loaded";
                const handle = String(
                    card?.dataset?.laDnProductHandle ||
                    this.getRenderedSearchHandleForItem(item) ||
                    ""
                ).trim();
                if (!card && !isCardLoaded) {
                    return;
                }
                const hit = (handle && fullHitsByHandle.get(handle)) || null;
                const fallbackProductData = this.parseSearchTrackingProductData(
                    card?.dataset?.trackingProduct
                );
                const resolvedTealiumEntry = typeof window.poResolveTealiumProductEntry === "function"
                    ? window.poResolveTealiumProductEntry({
                        productHandle: handle,
                        variantSku: card?.dataset?.sku || "",
                        productId: card?.dataset?.laDnProductId || hit?.objectID || hit?.id || "",
                        variantId: "",
                        productData: fallbackProductData
                    })
                    : null;
                const productKey = String(
                    resolvedTealiumEntry?.key ||
                    card?.dataset?.tealiumProductKey ||
                    card?.dataset?.sku ||
                    card?.dataset?.upc ||
                    this.resolveSearchHitUpc(hit, card) ||
                    card?.dataset?.laDnProductId ||
                    hit?.objectID ||
                    hit?.id ||
                    handle
                ).trim();

                if (!productKey) {
                    return;
                }

                const productPayload = {
                    ...(resolvedTealiumEntry?.data && typeof resolvedTealiumEntry.data === "object"
                        ? resolvedTealiumEntry.data
                        : {}),
                    ...(fallbackProductData && typeof fallbackProductData === "object"
                        ? fallbackProductData
                        : {})
                };
                const resolvedUpc = String(
                    productPayload.Upc ||
                    card?.dataset?.upc ||
                    this.resolveSearchHitUpc(hit, card) ||
                    ""
                ).trim();

                const renderedPrice = this.formatSearchTealiumMinorUnitPrice(
                    card?.dataset?.laDnProductPrice || tile?.dataset?.laDnProductPrice
                );
                const renderedPriceFull = this.formatSearchTealiumMinorUnitPrice(
                    card?.dataset?.laDnProductCompareAtPrice || tile?.dataset?.laDnProductCompareAtPrice
                );
                const fallbackHitPrice = this.formatSearchTealiumMinorUnitPrice(
                    this.getDirectHitPrice(hit, priceSortDirection)
                );
                const fallbackHitFullPrice = this.formatSearchTealiumMinorUnitPrice(
                    hit?.compare_at_price
                );
                const fallbackPayloadPrice = typeof formatTealiumPriceValue === "function"
                    ? formatTealiumPriceValue(productPayload.Price)
                    : String(productPayload.Price || "").trim();
                const fallbackPayloadPriceFull = typeof formatTealiumPriceValue === "function"
                    ? formatTealiumPriceValue(productPayload.PriceFull)
                    : String(productPayload.PriceFull || "").trim();
                const price = this.getFirstSearchUsablePrice(
                    renderedPrice,
                    fallbackHitPrice,
                    fallbackPayloadPrice
                );
                let priceFull = this.getFirstSearchUsablePrice(
                    renderedPriceFull,
                    fallbackHitFullPrice,
                    fallbackPayloadPriceFull,
                    price
                );

                if (!priceFull) {
                    priceFull = price;
                }

                if (Number.isFinite(Number(price)) && Number.isFinite(Number(priceFull)) && Number(priceFull) < Number(price)) {
                    priceFull = price;
                }

                productPayload.Status = String(
                    card?.dataset?.laDnProductAvailable === "true"
                        ? "Available"
                        : card?.dataset?.laDnProductAvailable === "false"
                            ? "Out-of-stock"
                            : hit?.inventory_available === true
                                ? "Available"
                                : hit?.inventory_available === false
                                    ? "Out-of-stock"
                                    : productPayload.Status || "Out-of-stock"
                ).trim();
                productPayload.LensType = String(productPayload.LensType || "PLANO");
                productPayload.Price = price;
                productPayload.PriceFull = priceFull;
                productPayload.Handle = handle || productPayload.Handle || "";
                delete productPayload.PriceNet;
                delete productPayload.PriceFullNet;

                const resolvedCategory = String(
                    productPayload.Category ||
                    card?.dataset?.tealiumCategory ||
                    card?.dataset?.category ||
                    tile?.dataset?.tealiumCategory ||
                    tile?.dataset?.category ||
                    hit?.meta?.custom?.prod_hierarchy_1 ||
                    hit?.product_type ||
                    ""
                ).trim();

                if (resolvedCategory) {
                    productPayload.Category = this.normalizeSearchTealiumCategory(resolvedCategory);
                }

                if (resolvedUpc) {
                    productPayload.Upc = resolvedUpc;
                }

                products[productKey] = productPayload;
            });

            if (typeof sanitizeProducts === "function") {
                sanitizeProducts(products);
            }

            return products;
        }

        syncSearchProductsForTracking() {
            const products = this.buildSearchProductsPayload();
            window.tealium = window.tealium || {};

            if (Object.keys(products).length > 0) {
                window.tealium.products = products;
            } else if (window.tealium.products) {
                delete window.tealium.products;
            }

            return products;
        }

        resolveSerpSearchTrackingContext(preferredQuery = "") {
            const ctx = this.getStoredSearchContext();
            const fallbackQuery = this.normalizeSearchQuery(this.getSearchQueryFromUrl());
            const queryRaw = this.normalizeSearchQuery(
                this.getCurrentSearchQuery(preferredQuery) || fallbackQuery
            );
            const queryLower = this.normalizeLower(queryRaw);
            const ctxKeywordRaw = this.normalizeSearchQuery(ctx.keyword || "");
            const ctxKeyword = this.normalizeLower(ctxKeywordRaw);
            const useStoredContext = Boolean(ctxKeyword) && (!queryLower || ctxKeyword === queryLower);
            const keyword = useStoredContext ? ctxKeyword : queryLower;
            const keyActual = this.normalizeLower(
                useStoredContext
                    ? (ctx.keyActual || ctxKeywordRaw || queryRaw)
                    : queryRaw
            );
            const searchType = this.normalizeLower(
                useStoredContext ? (ctx.type || "text") : "text"
            );

            return {
                queryRaw,
                queryLower,
                keyword,
                keyActual,
                searchType,
            };
        }

        pushSearchVirtualPageView(payload = {}) {
            if (typeof window.tealium_data2track?.push !== "function") {
                return false;
            }

            const localPayload = this.cloneTealiumPayload(payload);
            const signature = [
                localPayload.Page_Type || "",
                localPayload.Search_Keyword || "",
                localPayload.Search_KeyActual || "",
                localPayload.Search_Type || "",
                localPayload.Search_View || "",
                localPayload.Search_ResultItemsQnt || "",
                localPayload.Search_FacetValues_String || "",
            ].join("|");
            const now = Date.now();

            if (
                signature &&
                signature === this._lastSearchPageViewSignature &&
                now - (this._lastSearchPageViewSignatureTs || 0) < 1500
            ) {
                return false;
            }

            this._lastSearchPageViewSignature = signature;
            this._lastSearchPageViewSignatureTs = now;
            window.__poSearchPageViewTracked = true;
            window.tealium_data2track.push(localPayload);
            return true;
        }

        // ---- Tealium ----
        sendTealiumData = (data, options = {}) => {
            if (!window.tealium_data2track || typeof window.tealium_data2track.push !== "function") {
                console.warn("Tealium data tracking function is not defined.");
                return;
            }

            if (this.template !== "search") return;

            const {
                queryRaw,
                queryLower,
                keyword,
                keyActual,
                searchType,
            } = this.resolveSerpSearchTrackingContext(data?.query);
            if (!queryRaw || !keyword) return;

            const liveQueryKey = this.getSearchQueryKey();
            if (liveQueryKey && liveQueryKey !== queryLower) return;

            if (queryLower === this._lastSerpQuery) return;

            const lastParams =
                data?.params ||
                this.fetchParams ||
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.params;
            let parsedParams = [];

            try {
                const fetchParams = String(lastParams || "")
                    .split("&")
                    .filter((param) => param.includes("facetFilters"));
                const decoded = decodeURIComponent(fetchParams[0] || "");
                parsedParams = fetchParams[0] ? JSON.parse(decoded.split("=")[1]) : [];
            } catch (e) {
                console.error("Error parsing facet filters from params:", e);
            }

            const facetStr = this.formatFacetFiltersToString(parsedParams);

            const { productCount, supportCount } = this.getSerpCounts(
                this.getDisplayProductHits(),
                this.getActiveArticleCount(queryRaw)
            );
            const resultItemsQnt = this.getSearchResultItemsQnt(
                productCount,
                supportCount
            );

            const pageDesign = this.getSerpPageDesign(productCount, supportCount);
            const products = this.syncSearchProductsForTracking();
                const shouldRetryForProducts =
                    Number(productCount || 0) > 0 &&
                    (
                        Object.keys(products || {}).length === 0 ||
                        !this.hasSearchProductsReadyForTracking(products)
                    ) &&
                    Number(options.attempt || 0) < 10;

            if (shouldRetryForProducts) {
                clearTimeout(this._pendingSerpTealiumRetry);
                this._pendingSerpTealiumRetry = setTimeout(() => {
                    this.sendTealiumData(data, {
                        attempt: Number(options.attempt || 0) + 1,
                    });
                }, options.attempt ? 200 : 500);
                return;
            }

            const payload = {
                ...this.getBaseSerpTealiumPayload(),
                id: "VirtualPage-View",
                Page_Type: "Search",
                Page_Section1: "Search",
                Page_Design: pageDesign,
                Events_SearchRun: "1",
                Search_Keyword: keyword,
                Search_KeyActual: keyActual,
                Search_Type: searchType,
                Search_View: "SERP",
                Search_ResultItemsQnt: String(resultItemsQnt),
            };

            applySearchFilteringTealiumFields(payload, facetStr);

            if (Object.keys(products || {}).length > 0) {
                payload.Products = products;
            }

            if (this.pushSearchVirtualPageView(payload)) {
                clearTimeout(this._pendingSerpTealiumRetry);
                this._pendingSerpTealiumRetry = null;
                this._lastSerpQuery = queryLower;
            }
        };

        updateItemsCount() {
            if (this.template !== "search") return;
            this.syncSearchResultCounts();
        }

        getArticleLink(item = {}) {
            const directLink =
                item?.url ||
                item?.permalink ||
                item?.link ||
                item?.path ||
                item?.full_url;
            if (directLink) return directLink;

            const articleHandle =
                item?.handle ||
                item?.article_handle ||
                item?.slug;
            const blogHandle =
                item?.blog?.handle ||
                item?.blog_handle ||
                item?.blogHandle;

            if (blogHandle && articleHandle) {
                return `${Shopify.routes.root}blogs/${blogHandle}/${articleHandle}`;
            }

            return "";
        }

        loadArticles() {
            const listEl = document.querySelector(
                '#po-tab-panel-articles .po-articles-list'
            );
            if (!listEl) return;

            const helper = window.algoliaShopify?.instantsearch?.search?.helper;
            const query = this.getCurrentSearchQuery(helper?.state?.query);
            const queryKey = this.getSearchQueryKey(query);

            if (this._articlesLoadedForQuery !== queryKey) {
                this.resetArticlesState();
                this.syncSearchResultCounts(query);
            }

            if (this.articlesLoaded && this._articlesLoadedForQuery === queryKey) {
                return;
            }

            const url = this.getQueryUrl();
            const params = new URLSearchParams({
                query,
                hitsPerPage: "8",
            });

            const req = {
                indexName: this.articleIndexName,
                params: params.toString(),
            };

            fetch(url, {
                method: "POST",
                body: JSON.stringify({ requests: [req] }),
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.results?.[0];
                    if (!result) return;

                    if (this.getSearchQueryKey(this.getCurrentSearchQuery()) !== queryKey) return;

                    const hits = result.hits || [];
                    const articleCount = result.nbHits || 0;
                    this.articleCount = articleCount;
                    this._articlesLoadedForQuery = queryKey;
                    this.syncSearchResultCounts(query);

                    listEl.innerHTML = "";

                    if (!hits.length) {
                        listEl.innerHTML =
                            '<li class="tw-text-sm tw-text-neutral-dark">Nessun contenuto trovato.</li>';
                        this.articlesLoaded = true;
                        this._articlesLoadedForQuery = queryKey;
                        document.dispatchEvent(
                            new CustomEvent("articles:loaded", {
                                detail: { count: articleCount },
                            })
                        );
                        return;
                    }

                    hits.forEach((item) => {
                        const itemLink = this.getArticleLink(item);

                        const rawImage =
                            item.image ||
                            (window.algoliaShopify?.helpers?.compactImage
                                ? window.algoliaShopify.helpers.compactImage(item)
                                : "");

                        const bgStyle = rawImage
                            ? `background-image: url('${rawImage}'); background-size: cover; background-position: center;`
                            : "";

                        const articleTitle = escapeHtml(
                            item.title ||
                            item.name ||
                            ""
                        );

                        const li = document.createElement("li");
                        li.className = "tw-w-full";

                        li.innerHTML = `
                        <a href="${itemLink || "#"}" class="tw-block tw-w-full tw-h-full !hover:tw-no-underline">
                            <div
                            class="tw-p-po-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                            style="${bgStyle}"
                            >
                            </div>
                            <div class="tw-flex tw-items-start tw-gap-po-spacings-md tw-self-stretch tw-p-po-spacings-base">
                                <span
                                class="!tw-typo-po-h5
                                        !tw-text-po-h5
                                        tw-font-normal
                                        tw-leading-[32px]
                                        tw-tracking-[1.4px]
                                        tw-text-po-default"
                                >
                                    ${articleTitle}
                                </span>
                            </div>
                        </a>
                        `;

                        if (itemLink) {
                            li.classList.add("tw-cursor-pointer");
                        } else {
                            li.querySelector("a")?.setAttribute("aria-disabled", "true");
                        }

                        listEl.appendChild(li);
                    });

                    this.articlesLoaded = true;
                    this._articlesLoadedForQuery = queryKey;
                    document.dispatchEvent(
                        new CustomEvent("articles:loaded", {
                            detail: { count: articleCount },
                        })
                    );
                })
                .catch((err) => {
                    console.error("Error fetching Algolia articles:", err);
                });
        }

        updateTabsAndOptionsVisibility() {
            if (this.template !== "search") return;

            const tabsContainer = document.querySelector(".po-search-tabs-container");
            const optionsContainer = document.querySelector(".po-search-options-container");

            if (!tabsContainer && !optionsContainer) return;

            const itemsCount = this.getDisplayProductHits();
            const articleCount = this.getActiveArticleCount();

            if (tabsContainer) tabsContainer.classList.remove("tw-hidden");
            if (optionsContainer) optionsContainer.classList.remove("tw-hidden");

            if (itemsCount > 0 && articleCount === 0) {
                if (tabsContainer) tabsContainer.classList.add("tw-hidden");
                if (typeof window.poSwitchTab === "function") {
                    window.poSwitchTab("products");
                }
            }

            if (itemsCount === 0 && articleCount > 0) {
                if (tabsContainer) tabsContainer.classList.add("tw-hidden");
                if (optionsContainer) optionsContainer.classList.add("tw-hidden");

                if (typeof window.poSwitchTab === "function") {
                    window.poSwitchTab("articles");
                }
            }
        }

        init() {
            const registerAlgoliaHooks = () => {
                if (this._algoliaHooksRegistered || !window.algoliaShopify?.hooks?.registerHook) {
                    return;
                }

                this._algoliaHooksRegistered = true;

                // beforeInstantSearchConfigurationOptions
                window.algoliaShopify?.hooks?.registerHook(
                    "beforeInstantSearchConfigurationOptions",
                    (options) => {
                        const defaultIndexName = this.getDefaultProductsIndex();
                        return {
                            ...options,
                            indexName: defaultIndexName,
                            searchFunction: async (searchFunctionHelper) => {
                                if (this.template === "collection") await this.loadNonRxVariantFilter();
                                const page = searchFunctionHelper.getPage();
                                const configuredHitsPerPage = this.getProductsPerPage();
                                const resolvedSearchQuery =
                                    this.template === "search"
                                        ? this.getCurrentSearchQuery(searchFunctionHelper?.state?.query)
                                        : this.normalizeSearchQuery(searchFunctionHelper?.state?.query);
                                const helperIndexName =
                                    window.poAlgoliaUtils?.normalizeIndexName?.(
                                        searchFunctionHelper?.state?.index || defaultIndexName
                                    ) || defaultIndexName;

                                if (helperIndexName) {
                                    if (typeof searchFunctionHelper.setIndex === "function") {
                                        searchFunctionHelper.setIndex(helperIndexName);
                                    } else if (searchFunctionHelper.state) {
                                        searchFunctionHelper.state.index = helperIndexName;
                                    }

                                    window.__poPlpCurrentIndexName = helperIndexName;
                                    this.loadMoreIndex = helperIndexName;
                                }

                                if (configuredHitsPerPage) {
                                    searchFunctionHelper.setQueryParameter(
                                        "hitsPerPage",
                                        configuredHitsPerPage
                                    );
                                }

                                if (this.template === "collection") {
                                    const path = window.location.pathname || "";
                                    const pathHandle = path.includes("/collections/")
                                        ? path.split("/collections/")[1]?.split("/")[0]
                                        : null;
                                    const currentCollectionHandle =
                                        window.collection?.handle ||
                                        pathHandle;
                                    const plpCollectionHandle =
                                        window.collection?.plp_handle ||
                                        currentCollectionHandle;
                                    const queryCollectionHandle =
                                        plpCollectionHandle ||
                                        currentCollectionHandle;
                                    const isUngroupedCollection = this.isCollectionUngrouped();
                                    const dynamicRuleContexts = sessionStorage.getItem('ruleContexts');
                                    const parsedRuleContexts = dynamicRuleContexts
                                        ? JSON.parse(dynamicRuleContexts)
                                        : null;
                                    const ruleContexts = [];

                                    if (!isUngroupedCollection) ruleContexts.push("grouped");
                                    if (plpCollectionHandle) ruleContexts.push(plpCollectionHandle);
                                    if (currentCollectionHandle && currentCollectionHandle !== plpCollectionHandle) {
                                        ruleContexts.push(currentCollectionHandle);
                                    }
                                    if (parsedRuleContexts) ruleContexts.push(parsedRuleContexts);
                                    searchFunctionHelper.setQueryParameter(
                                        "query",
                                        String(searchFunctionHelper?.state?.query || "").trim() || undefined
                                    );
                                    searchFunctionHelper.setQueryParameter("distinct", isUngroupedCollection ? 0 : 1);
                                    searchFunctionHelper.setQueryParameter("ruleContexts", ruleContexts.length ? ruleContexts : undefined);
                                    searchFunctionHelper.setQueryParameter("upc_collapsed", undefined);
                                    searchFunctionHelper.setQueryParameter("facets", ["*"]);
                                    applyMinimalHitPayloadToHelper(searchFunctionHelper, {
                                        includePriceData: this.isPriceSortIndex(
                                            searchFunctionHelper?.state?.index || this.getLastIndexName()
                                        ),
                                    });
                                    const collectionFilter = this.getCurrentCollectionFilter(queryCollectionHandle);
                                    if (collectionFilter) {
                                        searchFunctionHelper.setQueryParameter(
                                            "filters",
                                            collectionFilter
                                        );
                                    }
                                } else {
                                    const hasSearchQuery = Boolean(resolvedSearchQuery);
                                    const searchRuleContexts = hasSearchQuery
                                        ? ["upc_collapsed"]
                                        : ["POPULAR_ITEMS", "upc_collapsed"];
                                    searchFunctionHelper.setQueryParameter(
                                        "query",
                                        hasSearchQuery ? resolvedSearchQuery : undefined
                                    );
                                    searchFunctionHelper.setQueryParameter("distinct", false);
                                    searchFunctionHelper.setQueryParameter("ruleContexts", searchRuleContexts);
                                    searchFunctionHelper.setQueryParameter("upc_collapsed", undefined);
                                    applyMinimalHitPayloadToHelper(searchFunctionHelper, {
                                        includePriceData: this.isPriceSortIndex(
                                            searchFunctionHelper?.state?.index || this.getLastIndexName()
                                        ),
                                    });
                                }
                                searchFunctionHelper.setPage(page);
                                searchFunctionHelper.search();
                            },
                        };
                    }
                );

                // beforeInstantSearchProductTemplate
                window.algoliaShopify?.hooks?.registerHook(
                    "beforeInstantSearchProductTemplate",
                    (_defaultTemplate, hit, htmlOrTrackSearchAttribution, _components, maybeTrackSearchAttribution) => {
                        const groupKey = hit?.meta?.custom?.x_groupkey;
                        const usesCurrentHookSignature = Boolean(_components && typeof _components === "object")
                            || maybeTrackSearchAttribution !== undefined;
                        const trackSearchAttribution = usesCurrentHookSignature
                            ? maybeTrackSearchAttribution
                            : htmlOrTrackSearchAttribution;
                        window.__poPlpCurrentIndexName = this.getLastIndexName();
                        this.fetchParams =
                            window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.params;
                        this.firstPageHits.push(hit);
                        if (this.template === "search") {
                            this.searchLoadMoreOffset = this.firstPageHits.length;
                        }
                        this.cachePlpFullHits([hit]);

                        const setClickAttribution = () => {
                            try {
                                if (typeof trackSearchAttribution === "function") {
                                    trackSearchAttribution(hit);
                                }
                            } catch (error) {
                                console.warn("[po-algolia] trackSearchAttribution failed", error);
                            }

                            const fallbackClickTracker = window.poAlgoliaInsights?.trackClickedItem;
                            if (typeof fallbackClickTracker === "function") {
                                fallbackClickTracker.call(window.poAlgoliaInsights, {
                                    item: {
                                        objectID: hit?.objectID,
                                        queryID: hit?.queryID,
                                        indexName: hit?.index || hit?.indexName || this.getLastIndexName(),
                                        position: hit?.productPosition,
                                        productId: hit?.id,
                                        handle: hit?.handle,
                                        source: "plp",
                                    },
                                    source: "plp",
                                    eventName: "Product Clicked",
                                });
                            }
                        };

                        this.addGroupKey(groupKey);

                        const prodCard = this.addProductCard(hit, setClickAttribution);

                        if (this.firstPageHits.length === this.lastNbHits) {
                            if (this.groupKeys.length > 0 && this.shouldLoadProductGroupsForIndex()) {
                                this.loadProductGroups(this.groupKeys);
                            } else {
                                this.onCardsLoaded(this.productRenderToken);
                            }
                        }
                        return prodCard;
                    }
                );

                // Main/No Result templates
                window.algoliaShopify?.hooks?.registerHook(
                    "beforeInstantSearchMainTemplate",
                    (_defaultTemplate, data, html) => {
                        const initialGridSkeleton = this.getInitialGridSkeletonMarkup(html);
                        const showCollectionSkeleton = this.template === "collection" && Boolean(initialGridSkeleton);
                        const translations = getAlgoliaTranslations();

                        return html`
                            <div id="po-algolia-header-wrapper"></div>
                            <div class="ais-page" style="${showCollectionSkeleton ? 'display: block;' : 'display: none;'}">
                                <div class="ais-input" style="display: none;">
                                    <div class="ais-search-box-container"></div>
                                    <div class="ais-input-button">
                                        <div class="ais-clear-input-icon"></div>
                                    </div>
                                </div>
                                <div class="ais-facets-button" style="display: none;">Show filters</div>
                                <div class="filters-wrapper" style="display: none">
                                    <div class="ais-clear-refinements-container"></div>
                                    <div class="ais-current-refined-values-container"></div>
                                    <div class="
                    ais-dynamic-widgets-container [&_.ais-Panel:not(hidden)]:tw-border-t-1
                    [&_.ais-Panel:not(hidden)]:tw-py-po-spacings-lg [&_.ais-Panel]:tw-border-neutral-lighter
                    [&_label]:tw-font-heading [&_label]:tw-text-[16px]">
                                    </div>
                                </div>
                                <div>
                                    <div class="ais-search-header" style="display: none;">
                                        <div class="ais-stats-container"></div>
                                        <div class="ais-change-display">
                      <span class="ais-change-display-block ais-change-display-selected">
                        <i class="fa fa-th-large"></i>
                      </span>
                                            <span class="ais-change-display-list">
                        <i class="fa fa-th-list"></i>
                      </span>
                                        </div>
                                        <div class="ais-sort po-original-sort">
                                            ${data.multipleSortOrders
                                                    ? html`${translations.sortBy}
                                                    <span class="ais-sort-orders-container"></span>`
                                                    : html`${translations.sortBy}
                                            ${translations.relevance}`}
                                        </div>
                                    </div>
                                    <div id="po-tab-panel-products">
                                        ${initialGridSkeleton}
                                        <div class="ais-hits-container ais-results-as-block" style="${showCollectionSkeleton ? 'display: none;' : ''}"></div>
                                        <div class="ais-pagination-container"></div>
                                    </div>
                                    <div id="po-tab-panel-articles" class="tw-hidden tw-py-po-spacings-base">
                                        <ul class="po-articles-list tw-list-none tw-grid tw-grid-cols-1 sd:tw-grid-cols-2 ld:tw-grid-cols-4 tw-gap-po-spacings-base"></ul>
                                    </div>
                                </div>
                            </div>
                        `;
                    }
                );

                window.algoliaShopify?.hooks?.registerHook(
                    "beforeInstantSearchNoResultTemplate",
                    (_defaultTemplate, html) => {
                        this.showAisPage();

                        // set span in japan market 
                        // jp not need link for clear filters and change input
                        const lang = document.documentElement.lang;
                        const useSpan = lang === "ja-JP" || lang === "en-JP";

                        const translations = getAlgoliaTranslations();
                        const ClearFilters = useSpan
                        ? html`<span class="ais-hit-empty--clear-filters tw-font-heading">${translations.clearFilters}</span>`
                        : html`<a class="ais-hit-empty--clear-filters tw-font-heading">${translations.clearFilters}</a>`;

                        const ChangeInput = useSpan
                        ? html`<span class="ais-hit-empty--clear tw-font-heading">${translations.changeInput}</span>`
                        : html`<a class="ais-hit-empty--clear tw-font-heading">${translations.changeInput}</a>`;
                        // set html template no resultss
                        return html`
                            <div class="ais-hit-empty">
                                <div class="ais-hit-empty--title tw-font-heading">
                                    ${translations.noResultFound}
                                </div>
                                <div class="ais-hit-empty--clear tw-font-heading">
                                    ${translations.try} ${ClearFilters} ${translations.or} ${ChangeInput}
                                </div>
                            </div>
                        `;
                    }
                );

                // Stats template - update counters and push to Tealium (existing behavior)
                window.algoliaShopify?.hooks?.registerHook(
                    "beforeInstantSearchStatsTemplate",
                    (_defaultTemplate, data) => {
                        const statsData = data && typeof data === "object" ? data : {};
                        const lastResults = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults;
                        const statsParams = statsData.params || this.fetchParams || lastResults?.params || "";
                        const statsNbHits = Number(statsData.nbHits) || 0;
                        const currentQuery = (statsData.query || this.getCurrentSearchQuery()).toString();
                        const currentQueryKey = this.getSearchQueryKey(currentQuery);
                        const statsIndexName = this.getLastIndexName();
                        const currentFacetFilters = this.normalizeFacetFilters(
                            window.algoliaShopify?.instantsearch?.search?.helper?.state?.facetFilters
                        );
                        const cachedDisplayProductHits = this.filteredResultCountMap.get(
                            this.getFacetCacheKey(currentFacetFilters, statsIndexName, currentQuery)
                        );
                        if (this.template === "search" && currentQueryKey !== this._articlesLoadedForQuery) {
                            this.resetArticlesState();
                        }

                        this.maxPages = data.nbPages;
                        this.lastNbHits = Math.min(data.nbHits, this.getProductsPerPage());
                        this.lastMaxHits = data.nbHits;
                        this.productHits = Number(data.nbHits) || 0;
                        this.displayProductHits = Number(cachedDisplayProductHits);
                        if (!Number.isFinite(this.displayProductHits)) {
                            this.displayProductHits = this.productHits;
                        }
                        this.totalHits = this.getDisplayProductHits();
                        this.hasResultStats = true;
                        this.firstPageRendered = false;
                        this.prepareProductRender();

                        this.syncVisibleResultCounts(currentQuery);
                        if (this.template === "search") {
                            this.syncSearchDisplayProductCount({
                                params: statsParams,
                                indexName: statsIndexName,
                                facetFilters: currentFacetFilters,
                                query: currentQuery,
                            });
                        } else if (this.isCollectionUngrouped()) {
                            this.syncUngroupedCollectionDisplayCount({
                                params: statsParams,
                                indexName: statsIndexName,
                                facetFilters: currentFacetFilters,
                            });
                        }

                        setTimeout(() => {
                            this.sendTealiumData(statsData);
                        }, 500);

                        this.updateTabsAndOptionsVisibility();

                        if (this.template === "search") {
                            this.waitForElement(
                                "#po-tab-panel-articles .po-articles-list",
                                () => this.loadArticles()
                            );
                        }

                        return _defaultTemplate;
                    }
                );
            };

            document.addEventListener("algolia.hooks.initialize", registerAlgoliaHooks, { once: true });
            registerAlgoliaHooks();

            document.addEventListener("articles:loaded", (e) => {
                const currentQuery = this.getCurrentSearchQuery();
                const articleCount = this.getActiveArticleCount(currentQuery) || Number(e.detail?.count || 0);
                const items = this.getDisplayProductHits();
                this.syncSearchResultCounts(currentQuery);
                this.updateTabsAndOptionsVisibility();

                const tabsContainer = document.querySelector(".po-search-tabs-container");
                const optionsContainer = document.querySelector(".po-search-options-container");
                const productsPanel = document.getElementById("po-tab-panel-products");
                const articlesPanel = document.getElementById("po-tab-panel-articles");

                if (items > 0 && articleCount === 0) {
                    tabsContainer?.classList.add("tw-hidden");
                    optionsContainer?.classList.remove("tw-hidden");
                    productsPanel?.classList.remove("tw-hidden");
                    articlesPanel?.classList.add("tw-hidden");
                } else if (items === 0 && articleCount > 0) {
                    tabsContainer?.classList.add("tw-hidden");
                    optionsContainer?.classList.add("tw-hidden");

                    const productsPanel = document.getElementById("po-tab-panel-products");
                    const articlesPanel = document.getElementById("po-tab-panel-articles");
                    productsPanel?.classList.add("tw-hidden");
                    articlesPanel?.classList.remove("tw-hidden");

                    const empty = document.querySelector(".ais-hit-empty");
                    empty?.remove();
                    const aisHits = document.querySelector(".ais-page .ais-Hits");
                    aisHits?.classList.remove("ais-Hits--empty");
                } else {
                    tabsContainer?.classList.remove("tw-hidden");
                    optionsContainer?.classList.remove("tw-hidden");
                    productsPanel?.classList.remove("tw-hidden");
                    articlesPanel?.classList.add("tw-hidden");
                }

                this.showAisPage();
            });

            // ---- Local storage for search term ----
            this.setSearchLocalStorage();

            // ---- Filters change handler ----
            document.addEventListener("plp:filters-changed", (event) => {
                const {selFacets, facetsFilters, index, showResults, tealiumFacets, sendTealiumData} = event.detail || {};
                const eventSignature = JSON.stringify({ selFacets, facetsFilters, index, showResults });
                const now = Date.now();

                if (
                    eventSignature === this.lastFiltersEventSignature &&
                    now - this.lastFiltersEventAt < 250
                ) {
                    return;
                }

                this.lastFiltersEventSignature = eventSignature;
                this.lastFiltersEventAt = now;

                poPlpSortDebug("plp:filters-changed", {
                    index,
                    showResults,
                    facetsFilters,
                    selectedFacetNames: selFacets,
                });

                const facetStr = this.formatFacetFiltersToString(
                    tealiumFacets || facetsFilters || []
                );
                this.lastFacetStr = facetStr;

                // --- TEALIUM: track filters changed ---
                if (this.template === "search" && sendTealiumData) {
                    try {
                        const cachedCount = this.filteredResultCountMap.get(
                            this.getFacetCacheKey(facetsFilters || [], index)
                        );
                        const rawCount = Number(cachedCount ?? this.getDisplayProductHits() ?? this.lastMaxHits ?? 0);
                        const supportCount = this.getActiveArticleCount();
                        const resultItemsQnt = this.getSearchResultItemsQnt(
                            rawCount,
                            supportCount
                        );
                        const { keyword, keyActual, searchType } = this.resolveSerpSearchTrackingContext();
                        if (!keyword) {
                            return;
                        }
                        const filterPayload = {
                            ...this.getBaseSerpTealiumPayload(),
                            id: "Event",
                            Page_Type: "Search",
                            Page_Section1: "Search",
                            Event_Source: "SERP",
                            Search_Keyword: keyword,
                            Search_KeyActual: keyActual,
                            Search_Type: searchType,
                            Search_View: "SERP",
                            Search_ResultItemsQnt: String(resultItemsQnt),
                        };

                        applySearchFilteringTealiumFields(filterPayload, facetStr);

                        delete filterPayload.Page_Design;
                        delete filterPayload.Products;

                        const localFilterPayload = this.cloneTealiumPayload(filterPayload);
                        window.tealium_data2track.push(localFilterPayload);
                        this.lastFacetStr = "";
                    } catch (e) {
                        console.warn("Tealium push on filters-changed failed:", e);
                    }
                } else if (sendTealiumData) {
                    try {
                        const cachedCount = this.filteredResultCountMap.get(
                            this.getFacetCacheKey(facetsFilters || [], index)
                        );
                        const resultItemsQnt = String(
                            cachedCount ?? this.lastMaxHits ?? this.getDisplayProductHits() ?? 0
                        );
                        const filterPayload = {
                            ...this.getBaseTealiumPayload(),
                            id: "Event",
                            Event_Source: "PLP",
                            Events_SearchFiltering: "1",
                            Search_ResultItemsQnt: resultItemsQnt,
                            Search_FacetValues_String: facetStr,
                        };

                        delete filterPayload.Page_Design;
                        delete filterPayload.Products;
                        delete filterPayload.Search_Keyword;
                        delete filterPayload.Search_KeyActual;
                        delete filterPayload.Search_Type;
                        delete filterPayload.Search_View;

                        const localFilterPayload = this.cloneTealiumPayload(filterPayload);
                        window.tealium_data2track.push(localFilterPayload);
                        this.lastFacetStr = "";
                    } catch (e) {
                        console.warn("Tealium push on PLP filters-changed failed:", e);
                    }
                }

                if (showResults) {
                    this.loadFilteredProducts(selFacets, facetsFilters, index);
                } else {
                    const isSingleFilterGroup = Array.isArray(facetsFilters) && facetsFilters.length === 1;
                    this.loadFilteredFacets(selFacets, facetsFilters, index, {
                        syncResultCount: isSingleFilterGroup
                    });

                    if (!isSingleFilterGroup) {
                        this.loadFilteredResultCount(selFacets, facetsFilters, index);
                    }
                }
            });

            // ---- Header/filters UI setup ----
            this.waitForElement('#algolia-shopify-instantsearch', () => {
                const wrapper = document.querySelector("#po-algolia-header-wrapper");
                const template = document.querySelector("#po-algolia-collection-header");
                if (!wrapper || !template) return;

                const clone = document.importNode(template.content, true);
                const cloneContent = clone.querySelector("div");
                wrapper.replaceWith(cloneContent);
                this.syncVisibleResultCounts(this.getCurrentSearchQuery());

                const filterButtonWrapper = cloneContent.querySelector("po-modal-trigger");
                const filterButton = filterButtonWrapper?.querySelector("button");
                const observeTarget = document.querySelector(".po-custom-algolia-header");
                if (!filterButtonWrapper || !filterButton || !observeTarget) return;

                const isMobile = () => window.innerWidth < 768;
                const getHeaderHeight = () => {
                    const header = document.querySelector(".js-headerGroup") ||
                        document.getElementById("po-main-header-fixed") ||
                        document.querySelector("sticky-header");
                    const announcement = document.querySelector("sticky-announcement");
                    return (header?.offsetHeight || 0) + (announcement?.offsetHeight || 0);
                };

                let lastScroll = window.scrollY;
                let isFixed = false;

                window.addEventListener('scroll', () => {
                    if (!isFixed) return;
                    const spacing = isMobile() ? 24 : 16;
                    const isScrollingUp = window.scrollY < lastScroll;
                    filterButtonWrapper.style.top = `${(isScrollingUp ? getHeaderHeight() : 0) + spacing}px`;
                    if (!isMobile()) filterButtonWrapper.style.transition = 'top 0.5s ease';
                    lastScroll = window.scrollY;
                }, { passive: true });

                new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            filterButtonWrapper.classList.remove("tw-fixed", "is-visible", "is-sticky-transitioning");
                            filterButtonWrapper.style.position = filterButtonWrapper.style.top = "";
                            filterButton.classList.remove("tw-shadow-md");
                            isFixed = false;
                        } else {
                            filterButtonWrapper.classList.add("tw-fixed");
                            filterButton.classList.add("tw-shadow-md");
                            isFixed = true;
                        }
                    });
                }, { threshold: 0.01 }).observe(observeTarget);

                const filtersWrapper = document.querySelector(".filters-wrapper");
                const filtersModalWrapper = document.querySelector(".po-filters-modal-wrapper");
                if (filtersWrapper && filtersModalWrapper) {
                    filtersModalWrapper.append(filtersWrapper);
                    filtersWrapper.style.display = "block";
                } else if (filtersWrapper) {
                    filtersWrapper.style.display = "none";
                }
                this.getSort?.();
            });
        }
    }

    const algoliaPoCustomSearch = new AlgoliaPoCustomSearch();
    window.algoliaPoCustomSearch = algoliaPoCustomSearch;

    window.poSwitchTab = function (tab) {
        const productsPanel = document.getElementById("po-tab-panel-products");
        const articlesPanel = document.getElementById("po-tab-panel-articles");

        if (!productsPanel || !articlesPanel) return;

        const isProducts = tab === "products";

        if (isProducts) {
            productsPanel.classList.remove("tw-hidden");
            articlesPanel.classList.add("tw-hidden");
        } else {
            productsPanel.classList.add("tw-hidden");
            articlesPanel.classList.remove("tw-hidden");

            window.algoliaPoCustomSearch?.loadArticles?.();
        }

        document.querySelectorAll(".po-tab-btn").forEach((btn) => {
            const btnTab = btn.getAttribute("data-tab");
            const isActive = btnTab === tab;

            btn.classList.toggle("is-active", isActive);

            if (isActive) {
                btn.classList.add(
                    "tw-border-b-2",
                    "tw-border-black",
                    "tw-po-font-bold",
                    "tw-text-petrol"
                );
                btn.classList.remove("tw-text-gray-500");
            } else {
                btn.classList.remove(
                    "tw-border-b-2",
                    "tw-border-black",
                    "tw-po-font-bold",
                    "tw-text-petrol"
                );
                btn.classList.add("tw-text-gray-500");
            }
        });

        const pagination = document.querySelector(".ais-pagination-container");
        if (pagination) {
            if (isProducts) {
                pagination.classList.remove("tw-hidden");
            } else {
                pagination.classList.add("tw-hidden");
            }
        }
    };

    if (!customElements.get('po-load-more-algolia')) {
        class PoLoadMoreCollection extends HTMLElement {
            connectedCallback() {
                this.button = this.querySelector('.js-load-more');
                this.currentPage = algoliaPoCustomSearch.getCurrentPage() || 1;
                this.handleVisibilitySync = this.handleVisibilitySync || (() => {
                    this.syncButtonVisibility();
                });

                document.addEventListener('algolia.instantSearch.onRender', this.handleVisibilitySync);
                document.addEventListener("plp:cards-loaded", this.handleVisibilitySync);
                document.addEventListener("plp:facet-count-updated", this.handleVisibilitySync);

                this.button?.addEventListener("click", async () => {
                    if (this.isSearchTemplate()) {
                        const didAppendHits = await this.fetchAlgoliaData(this.currentPage);
                        if (didAppendHits) {
                            this.currentPage++;
                        }
                        this.syncButtonVisibility();
                        return;
                    }

                    this.fetchAlgoliaData(this.currentPage);
                    this.currentPage++;
                    this.syncButtonVisibility();
                });

                this.handleFiltersHits = this.handleFiltersHits || (() => {
                    this.currentPage = algoliaPoCustomSearch.getCurrentPage() || 1;
                    this.button?.removeAttribute("disabled");
                    this.button?.removeAttribute("aria-busy");
                    this.syncButtonVisibility();
                });
                document.addEventListener("plp:filters-hits", this.handleFiltersHits);

                this.scheduleVisibilityBootstrap();
                this.syncButtonVisibility();
            }

            disconnectedCallback() {
                this.clearVisibilityBootstrap();

                if (this.handleVisibilitySync) {
                    document.removeEventListener('algolia.instantSearch.onRender', this.handleVisibilitySync);
                    document.removeEventListener("plp:cards-loaded", this.handleVisibilitySync);
                    document.removeEventListener("plp:facet-count-updated", this.handleVisibilitySync);
                }

                if (this.handleFiltersHits) {
                    document.removeEventListener("plp:filters-hits", this.handleFiltersHits);
                }
            }

            isSearchTemplate() {
                return algoliaPoCustomSearch.template === "search";
            }

            scheduleVisibilityBootstrap() {
                this.clearVisibilityBootstrap();

                [0, 150, 400, 900, 1600].forEach((delay) => {
                    const timerId = window.setTimeout(() => {
                        this.syncButtonVisibility();
                    }, delay);

                    this.visibilityBootstrapTimers ||= [];
                    this.visibilityBootstrapTimers.push(timerId);
                });
            }

            clearVisibilityBootstrap() {
                (this.visibilityBootstrapTimers || []).forEach((timerId) => {
                    window.clearTimeout(timerId);
                });
                this.visibilityBootstrapTimers = [];
            }

            getSearchLoadMoreBatchSize() {
                const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
                const columns = isDesktop
                    ? algoliaPoCustomSearch.getDesktopColumns()
                    : algoliaPoCustomSearch.getMobileColumns();

                return Math.max(1, Number(columns) || 1);
            }

            getSearchConsumedHitCount() {
                return Math.max(
                    0,
                    Number(
                        algoliaPoCustomSearch.searchLoadMoreOffset
                        || algoliaPoCustomSearch.lastNbHits
                        || 0
                    )
                );
            }

            getSearchTotalHitCount() {
                const displayHits = Number(
                    algoliaPoCustomSearch.getDisplayProductHits?.() || 0
                );
                const rawHits = Number(
                    algoliaPoCustomSearch.lastMaxHits
                    || algoliaPoCustomSearch.productHits
                    || 0
                );

                return Math.max(0, displayHits, rawHits);
            }

            setBtnHidden(hide) {
                if (this.button) {
                    hide ? this.button.style.display = "none" : this.button.style.removeProperty("display");
                }
            }

            syncButtonVisibility() {
                if (this.isSearchTemplate()) {
                    const renderedHits = Number(algoliaPoCustomSearch.getRenderedProductCount?.() || 0);

                    if (renderedHits <= 0) {
                        this.setBtnHidden(true);
                        return;
                    }

                    const maxPages = Number(algoliaPoCustomSearch.maxPages || 0);
                    const reachedLastPage = Number.isFinite(maxPages) && maxPages > 0
                        ? this.currentPage >= maxPages
                        : false;

                    this.setBtnHidden(
                        Boolean(algoliaPoCustomSearch.searchLoadMoreExhausted) || reachedLastPage
                    );
                    return;
                }

                this.setBtnHidden(this.currentPage >= algoliaPoCustomSearch.maxPages);
            }

            async fetchAlgoliaData(pageNumber, options = {}) {
                const renderToken = algoliaPoCustomSearch.productRenderToken;
                const url = new URL(`//${algoliaPoCustomSearch.algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`, window.location.origin);

                if (!algoliaPoCustomSearch.fetchParams) {
                    console.error("No fetch params found");
                    this.button.disabled = true;
                    return false;
                }

                this.button?.toggleAttribute("disabled", true);
                this.button?.setAttribute("aria-busy", "true");

                url.searchParams.set("x-algolia-api-key", algoliaPoCustomSearch.algoliaApiKey);
                url.searchParams.set("x-algolia-application-id", algoliaPoCustomSearch.algoliaAppId);

                const baseParams = new URLSearchParams(algoliaPoCustomSearch.fetchParams);
                const indexName = algoliaPoCustomSearch.loadMoreIndex || algoliaPoCustomSearch.getLastIndexName();

                window.__poPlpCurrentIndexName = indexName;

                try {
                    if (this.isSearchTemplate()) {
                        const hitsList = document.querySelector(".ais-Hits-list");
                        const groupKeys = new Set();
                        const params = new URLSearchParams(baseParams);
                        params.delete("offset");
                        params.delete("length");
                        params.set("page", String(pageNumber));

                        const req = {
                            indexName,
                            params: params.toString(),
                        };
                        const response = await fetch(url, {
                            body: JSON.stringify({ requests: [req] }),
                            method: "POST",
                        });
                        const data = await response.json();
                        if (renderToken !== algoliaPoCustomSearch.productRenderToken) return false;
                        const rawHits = data?.results?.[0]?.hits || [];

                        if (rawHits.length === 0) {
                            algoliaPoCustomSearch.searchLoadMoreExhausted = true;
                            return false;
                        }

                        algoliaPoCustomSearch.searchLoadMoreExhausted = false;
                        const hits = algoliaPoCustomSearch.isPriceSortIndex(indexName)
                            ? algoliaPoCustomSearch.sortHitsForPriceIndex(rawHits, indexName)
                            : rawHits;
                        algoliaPoCustomSearch.cachePlpFullHits(hits);

                        hits.forEach((hit) => {
                            const groupKey = hit.meta?.custom?.x_groupkey;
                            if (groupKey) {
                                groupKeys.add(groupKey);
                            }
                        });

                        if (hitsList) {
                            const renderedBefore = algoliaPoCustomSearch.getRenderedProductCount();
                            const productsPerPage = Math.max(
                                1,
                                Number(baseParams.get("hitsPerPage"))
                                    || Number(algoliaPoCustomSearch.productsPerPage)
                                    || 0
                            );
                            const batchSize = Math.min(hits.length, productsPerPage);
                            window.__poPlpPendingLoadMoreFromIndex = renderedBefore;
                            window.__poPlpPendingLoadMoreBatchSize = batchSize;
                            await algoliaPoCustomSearch.appendProductHitsInBatches(hitsList, hits, {
                                markLoadMoreBatch: true,
                                batchFromIndex: renderedBefore,
                                renderToken,
                            });
                            if (renderToken !== algoliaPoCustomSearch.productRenderToken) return false;
                            document.dispatchEvent(
                                new CustomEvent("plp:page-batch-appended", {
                                    detail: { fromIndex: renderedBefore, batchSize },
                                })
                            );
                        }

                        const hitsPerPage = Math.max(
                            1,
                            Number(baseParams.get("hitsPerPage"))
                            || Number(algoliaPoCustomSearch.getProductsPerPage?.())
                            || Number(algoliaPoCustomSearch.productsPerPage)
                            || 0
                        );
                        algoliaPoCustomSearch.searchLoadMoreOffset += rawHits.length;
                        if (rawHits.length < hitsPerPage) {
                            algoliaPoCustomSearch.searchLoadMoreExhausted = true;
                        }

                        algoliaPoCustomSearch.syncSearchResultCounts(
                            algoliaPoCustomSearch.getCurrentSearchQuery()
                        );
                        algoliaPoCustomSearch.updateTabsAndOptionsVisibility();
                        document.dispatchEvent(new CustomEvent("plp:facet-count-updated", {
                            detail: { nbHits: algoliaPoCustomSearch.getDisplayProductHits() }
                        }));

                        const nextGroupKeys = Array.from(groupKeys);
                        if (nextGroupKeys.length > 0 && algoliaPoCustomSearch.shouldLoadProductGroupsForIndex(indexName)) {
                            algoliaPoCustomSearch.loadProductGroups(nextGroupKeys, indexName);
                        } else {
                            window.__poPlpPendingLoadMoreFromIndex = null;
                            window.__poPlpPendingLoadMoreBatchSize = null;
                        }
                        return true;
                    } else {
                        const params = new URLSearchParams(baseParams);
                        params.delete("offset");
                        params.delete("length");
                        params.set("page", String(pageNumber));

                        const req = {
                            indexName,
                            params: params.toString(),
                        };
                        const response = await fetch(url, {
                            body: JSON.stringify({ requests: [req] }),
                            method: "POST",
                        });
                        const data = await response.json();
                        if (renderToken !== algoliaPoCustomSearch.productRenderToken) return false;
                        const groupKeys = new Set();
                        const hitsList = document.querySelector(".ais-Hits-list");
                        const rawHits = data?.results?.[0]?.hits || [];
                        const hits = algoliaPoCustomSearch.isPriceSortIndex(indexName)
                            ? algoliaPoCustomSearch.sortHitsForPriceIndex(rawHits, indexName)
                            : rawHits;

                        algoliaPoCustomSearch.cachePlpFullHits(hits);

                        hits.forEach((hit) => {
                            const groupKey = hit.meta?.custom?.x_groupkey;
                            if (groupKey) {
                                groupKeys.add(groupKey);
                            }
                        });

                        if (hitsList) {
                            const renderedBefore = algoliaPoCustomSearch.getRenderedProductCount();
                            const productsPerPage = Math.max(
                                1,
                                Number(algoliaPoCustomSearch.productsPerPage) || 0
                            );
                            const batchSize = Math.min(hits.length, productsPerPage);
                            window.__poPlpPendingLoadMoreFromIndex = renderedBefore;
                            window.__poPlpPendingLoadMoreBatchSize = batchSize;
                            await algoliaPoCustomSearch.appendProductHitsInBatches(hitsList, hits, {
                                markLoadMoreBatch: true,
                                batchFromIndex: renderedBefore,
                                renderToken,
                            });
                            if (renderToken !== algoliaPoCustomSearch.productRenderToken) return false;
                            document.dispatchEvent(
                                new CustomEvent("plp:page-batch-appended", {
                                    detail: { fromIndex: renderedBefore, batchSize },
                                })
                            );
                        }

                        algoliaPoCustomSearch.addBanners(this.currentPage);

                        const nextGroupKeys = Array.from(groupKeys);
                        if (nextGroupKeys.length > 0 && algoliaPoCustomSearch.shouldLoadProductGroupsForIndex(indexName)) {
                            algoliaPoCustomSearch.loadProductGroups(nextGroupKeys, indexName);
                        } else {
                            window.__poPlpPendingLoadMoreFromIndex = null;
                            window.__poPlpPendingLoadMoreBatchSize = null;
                        }
                        return rawHits.length > 0;
                    }
                } catch (error) {
                    if (renderToken !== algoliaPoCustomSearch.productRenderToken) return false;
                    console.error('Error:', error);
                    this.button.disabled = true;
                    return false;
                } finally {
                    if (renderToken === algoliaPoCustomSearch.productRenderToken) {
                        this.button?.removeAttribute("aria-busy");
                        if (this.isSearchTemplate()) {
                            this.button?.removeAttribute("disabled");
                            this.syncButtonVisibility();
                        } else if (this.currentPage < algoliaPoCustomSearch.maxPages) {
                            this.button?.removeAttribute("disabled");
                        }
                    }
                }
            }


        }

        customElements.define("po-load-more-algolia", PoLoadMoreCollection);
    }
})();
